import { useState, useRef, useCallback, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useUser, useAuth } from "@clerk/clerk-react";
import { PhotoGalleryPanel } from "./PhotoGallery";
import { useCoauthorPresence } from "../hooks/useCoauthorPresence";
import CoauthorPresenceStrip from "../components/CoauthorPresenceStrip";

// --- Brand palette (Cyrano) ---
// Accent slots keep the `gold*` names for minimal diff — values are now on-palette
// pink-lavender (#d4adbb, the "comfort and warmth" colour from the brand guide).
const B = {
  navy: "#1C2951", navyMid: "#2a3d6b",
  gold: "#d4adbb", goldPale: "#f0e4ea", goldFaint: "#faf5f8",
  cream: "#FAF8F5", creamDark: "#F2EDE7",
  lavender: "#d4adbb", lavenderPale: "#f0e4ea",
  wg: "#6B6B6B", wgL: "#9a9a9a", wgP: "#e8e5e0", wgF: "#f3f1ed",
  tx: "#2c2c2c", txM: "#555", txL: "#888",
  w: "#fff",
  ok: "#2d8a56", okBg: "#edf7f0",
  err: "#b5392a",
};

const WPM = 150;
const cw = (h: string) => { const t = h.replace(/<[^>]*>/g,"").replace(/&nbsp;/g," ").replace(/\s+/g," ").trim(); return t ? t.split(" ").filter(Boolean).length : 0; };

// Format a native date input's ISO value (yyyy-mm-dd) into the readable long
// form used on the export cover, e.g. "Saturday, 5 April 2026" (service date,
// with weekday) or "2 June 1931" (birth/passing, no weekday). Parsed from parts
// so there's no timezone day-shift.
function fmtLongDate(iso: string, withWeekday: boolean): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return "";
  try {
    return new Date(y, m - 1, d).toLocaleDateString("en-AU", {
      ...(withWeekday ? { weekday: "long" as const } : {}),
      day: "numeric", month: "long", year: "numeric",
    });
  } catch { return ""; }
}

// A free-text date field with a calendar picker. Users can still type anything
// (a year on its own, "circa 1955", etc.); the calendar button opens the native
// date picker and fills the field with the readable long-date form.
function DateField({ value, onChange, placeholder, withWeekday }: { value: string; onChange: (v: string) => void; placeholder: string; withWeekday?: boolean }) {
  const dateRef = useRef<HTMLInputElement>(null);
  const openPicker = () => {
    const el = dateRef.current as any;
    if (!el) return;
    try { if (typeof el.showPicker === "function") el.showPicker(); else el.focus(); }
    catch { el.focus(); }
  };
  return (
    <div style={{ position: "relative" }}>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        /* 16px font avoids iOS zoom-on-focus; 12px vertical padding gives a ~44px tap height to match the calendar button. */
        style={{ width: "100%", padding: "12px 46px 12px 12px", border: `1px solid ${B.wgP}`, borderRadius: 6, fontSize: 16, fontFamily: "'Inter',system-ui,sans-serif", boxSizing: "border-box" }}
      />
      <button
        type="button"
        onClick={openPicker}
        aria-label="Pick a date"
        title="Pick a date from a calendar"
        style={{ position: "absolute", right: 2, top: 0, height: "100%", minHeight: 44, width: 44, display: "flex", alignItems: "center", justifyContent: "center", background: "none", border: "none", cursor: "pointer", color: B.navy, padding: 0 }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="4.5" width="18" height="16" rx="2"/><path d="M3 9.5h18M8 2.5v4M16 2.5v4"/></svg>
      </button>
      <input
        ref={dateRef}
        type="date"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => { const s = fmtLongDate(e.target.value, !!withWeekday); if (s) onChange(s); }}
        style={{ position: "absolute", right: 10, bottom: 0, width: 1, height: 1, opacity: 0, pointerEvents: "none", border: 0, padding: 0 }}
      />
    </div>
  );
}

// Tidy generated content for the editor: drop a redundant leading title
// (e.g. "**Eulogy for Jean Cooper**" / a heading node) and, for markdown-flavoured
// output, convert emphasis to HTML and wrap paragraphs — so the editor never
// shows raw ** markers. HTML content is left intact apart from the title strip.
const TITLE_RE = /(a\s+)?(eulogy|tribute)\b|in\s+loving\s+memory|^remembering\b|^the\s+life\s+of\b/i;
function cleanForEditor(raw: string, deceasedName: string): string {
  if (!raw) return raw || "";
  let s = String(raw);
  const nameLow = (deceasedName || "").trim().toLowerCase();
  const isTitle = (t: string) => { const x = t.trim(); return x.length > 0 && x.length <= 70 && (TITLE_RE.test(x) || (!!nameLow && x.toLowerCase() === nameLow)); };
  // 1) Drop leading empty/decorative nodes (e.g. an empty inserted blockquote
  //    that an old generation left sitting before the markdown title).
  s = s.replace(/^\s*(<(p|div|blockquote)[^>]*>\s*(<br\s*\/?>)?\s*<\/(p|div|blockquote)>\s*)+/i, "");
  // 2) Strip a leading title in any form: a heading node, a <p> that's only a
  //    title, OR an inline markdown/bold/heading title at the very start.
  s = s.replace(/^\s*<h[1-6][^>]*>[\s\S]*?<\/h[1-6]>\s*/i, "");
  s = s.replace(/^\s*<p[^>]*>([\s\S]*?)<\/p>\s*/i, (m, inner) => isTitle(String(inner).replace(/<[^>]+>/g, "")) ? "" : m);
  s = s.replace(/^\s*(<p[^>]*>)?\s*(<strong>|<b>)?\s*\*\*\s*([^*\n]+?)\s*\*\*\s*(<\/strong>|<\/b>)?\s*(<\/p>)?\s*/i,
                (m, p, _o, t) => (isTitle(t) ? (p ? "<p>" : "") : m));
  s = s.replace(/^\s*#{1,6}\s*([^\n<]+)\n+/i, (m, t) => isTitle(t) ? "" : m);
  // Decide structure on the *real* block tags that remain (before we turn
  // markdown emphasis into <strong>/<em>, which would otherwise look like HTML).
  const hasBlockHtml = /<(p|div|h[1-6]|br|blockquote)\b/i.test(s);
  // 3) Convert leftover markdown emphasis so raw ** never shows, even in HTML.
  s = s.replace(/\*\*\*(.+?)\*\*\*/g, "<strong><em>$1</em></strong>")
       .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
       .replace(/__(.+?)__/g, "<strong>$1</strong>");
  if (hasBlockHtml) return s.trim();
  // 4) Plain text: convert single emphasis and wrap paragraphs.
  s = s.replace(/(^|[^*\w])\*([^*\n]+?)\*(?!\w)/g, "$1<em>$2</em>")
       .replace(/(^|[^_\w])_([^_\n]+?)_(?!\w)/g, "$1<em>$2</em>");
  const paras = s.split(/\n\s*\n/).map(p => p.replace(/\s*\n\s*/g, " ").trim()).filter(Boolean);
  return paras.length ? paras.map(p => `<p>${p}</p>`).join("") : s.trim();
}
const ft = (w: number) => { const m=Math.floor(w/WPM), s=Math.round(((w%WPM)/WPM)*60); return m===0&&s===0?"0 min":m===0?`${s} sec`:s===0?`${m} min`:`${m} min ${s} sec`; };

interface EulogyVersion {
  id: string;
  label: string;
  time: string;
  active: boolean;
  words: number;
  content: string;
  style_variant?: string;
}

interface Contributor {
  initials: string;
  name: string;
}

interface NoteSection {
  title: string;
  colour: string;
  items: string[];
}

// 2026-05-10: expanded from 6 to 12 options + grouped (tone vs length).
// Labels are sent to /api/eulogy/refine and used as keys in the
// toneInstructions map there, so they MUST match the keys in
// api/eulogy/refine.ts exactly.
type ToneOption = { id: string; label: string; group: "tone" | "length" };
const TONES: ToneOption[] = [
  // Tone register
  { id: "warmer", label: "Warmer", group: "tone" },
  { id: "sombre", label: "More sombre", group: "tone" },
  { id: "formal", label: "More formal", group: "tone" },
  { id: "uplifting", label: "More uplifting", group: "tone" },
  { id: "poetic", label: "More poetic", group: "tone" },
  { id: "reverent", label: "More reverent", group: "tone" },
  { id: "intimate", label: "More intimate", group: "tone" },
  { id: "reflective", label: "More reflective", group: "tone" },
  { id: "lighter", label: "Lighter touch", group: "tone" },
  { id: "humour", label: "Add humour", group: "tone" },
  // Length
  { id: "shorter", label: "Shorter", group: "length" },
  { id: "longer", label: "More detail", group: "length" },
];

interface TBItem { c:string; ic:string; t:string; v?:string; s?:Record<string,any> }

const TB: { items: TBItem[] }[] = [
  { items: [
    { c:"bold", ic:"B", t:"Bold", s:{fontWeight:700} },
    { c:"italic", ic:"I", t:"Italic", s:{fontStyle:"italic"} },
    { c:"underline", ic:"U", t:"Underline", s:{textDecoration:"underline"} },
  ]},
  { items: [
    { c:"formatBlock", v:"<h2>", ic:"H", t:"Heading" },
    { c:"formatBlock", v:"<blockquote>", ic:"\u201C", t:"Quote", s:{fontFamily:"Georgia,serif",fontSize:17} },
    { c:"formatBlock", v:"<p>", ic:"\u00B6", t:"Paragraph", s:{fontSize:13} },
  ]},
  { items: [
    { c:"insertUnorderedList", ic:"\u2022", t:"Bullet list" },
    { c:"insertOrderedList", ic:"1.", t:"Number list", s:{fontSize:13} },
  ]},
  { items: [
    { c:"justifyLeft", ic:"\u2261", t:"Left", s:{fontSize:17} },
    { c:"justifyCenter", ic:"\u2261", t:"Centre", s:{fontSize:17,textAlign:"center" as const,display:"inline-block" as const,width:17} },
  ]},
  { items: [
    { c:"undo", ic:"\u21A9", t:"Undo", s:{fontSize:17} },
    { c:"redo", ic:"\u21AA", t:"Redo", s:{fontSize:17} },
  ]},
];

export default function CyranoEditor() {
  const { eulogyId } = useParams<{ eulogyId: string }>();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("sessionId") || "";
  const navigate = useNavigate();
  const { user } = useUser();
  const { getToken } = useAuth();

  const edRef = useRef<HTMLDivElement>(null);
  // Refs the page-leave flush reads without a stale closure: the latest Clerk
  // token and the id of the version currently being edited.
  const tokenRef = useRef<string | null>(null);
  const activeVersionIdRef = useRef<string | null>(null);
  const [wc, setWc] = useState(0);
  const [focus, setFocus] = useState(false);
  const [save, setSave] = useState<"saved"|"saving"|"draft">("saved");
  const [act, setAct] = useState<Record<string,boolean>>({});
  const [title, setTitle] = useState("");
  const [deceasedName, setDeceasedName] = useState("");
  const [preferredName, setPreferredName] = useState("");
  // The name to address the deceased by: their preferred/known-as name (e.g.
  // "Jeanne"), falling back to the first token of the full name (e.g. "Jean").
  const displayFirst = (preferredName.trim() || (deceasedName ? deceasedName.split(" ")[0] : "")).trim();
  const [panel, setPanel] = useState("refine");
  const [mobileSb, setMobileSb] = useState(false);
  const [customReq, setCustomReq] = useState("");
  const [selTone, setSelTone] = useState<string|null>(null);
  // 2026-05-10: "In the style of [orator]" refinement input
  const [selStyle, setSelStyle] = useState("");
  // 2026-05-10: resizable splitter between editor and sidebar (desktop only).
  // Persisted to localStorage so the user's preferred width survives reloads.
  // Mobile uses the existing bottom-sheet, no splitter.
  const SB_WIDTH_MIN = 240;
  const SB_WIDTH_MAX = 600;
  const SB_WIDTH_DEFAULT = 300;
  const [sbWidth, setSbWidth] = useState<number>(() => {
    if (typeof window === "undefined") return SB_WIDTH_DEFAULT;
    const saved = window.localStorage.getItem("cyrano_editor_sb_width");
    const n = saved ? parseInt(saved, 10) : NaN;
    if (!Number.isFinite(n)) return SB_WIDTH_DEFAULT;
    return Math.max(SB_WIDTH_MIN, Math.min(SB_WIDTH_MAX, n));
  });
  const sbDraggingRef = useRef(false);
  const [generating, setGenerating] = useState(false);
  const [versions, setVersions] = useState<EulogyVersion[]>([]);
  const [genMsg, setGenMsg] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string|null>(null);
  const [contributors, setContributors] = useState<Contributor[]>([]);
  const [notes, setNotes] = useState<NoteSection[]>([]);
  // Seed from the ?type= the dashboard passes, so the loading spinner says the
  // right word ("Loading your tribute...") before the full load resolves it.
  const [docType, setDocType] = useState<"eulogy"|"tribute">(() => {
    try { return new URLSearchParams(window.location.search).get("type") === "tribute" ? "tribute" : "eulogy"; }
    catch { return "eulogy"; }
  });
  const [currentSessionId, setCurrentSessionId] = useState(sessionId);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportLoading, setExportLoading] = useState<string|null>(null);
  const [exportFields, setExportFields] = useState({ serviceDate: "", serviceVenue: "", birthDate: "", deathDate: "" });
  // Dates already captured (onboarding -> DB). When present we don't re-ask them at export.
  const [datesOnFile, setDatesOnFile] = useState<{ birth: string; passing: string }>({ birth: "", passing: "" });
  const [mobileToolbarExpanded, setMobileToolbarExpanded] = useState(false);
  const [testamentLicenseId, setTestamentLicenseId] = useState<string|null>(null);
  const [exportPhotos, setExportPhotos] = useState<{id:string;url:string;caption:string|null;isFeatured:boolean}[]>([]);
  const [coverPhotoId, setCoverPhotoId] = useState<string|null>(null); // null = Cyrano clouds (default)
  const [photosLoading, setPhotosLoading] = useState(false);
  const [quoteSuggestions, setQuoteSuggestions] = useState<{quote:string;author:string;source:string;theme:string;bridging:string;placement:string;anchorText?:string;placementNote:string}[]>([]);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [quotesError, setQuotesError] = useState<string|null>(null);
  const [quoteInserted, setQuoteInserted] = useState<number|null>(null);
  const [quoteCopiedClip, setQuoteCopiedClip] = useState<number|null>(null);
  const [poemSuggestions, setPoemSuggestions] = useState<{title:string;author:string;authorBirth:string;authorDeath:string;collection:string;copyright:string;fullText:string|null;excerpt:string;connection:string;whoShouldRead:string;readingNote:string;mood:string;links:{poetryFoundation:string;amazon:string}}[]>([]);
  const [poemsLoading, setPoemsLoading] = useState(false);
  const [poemsError, setPoemsError] = useState<string|null>(null);
  const [poemCopied, setPoemCopied] = useState<number|null>(null);
  const [qpSection, setQpSection] = useState<"quotes"|"poems">("quotes");
  // Phase 5 — finalisation + Memory Collection
  const [finalisedAt, setFinalisedAt] = useState<string|null>(null);
  const [, setFinalVersionId] = useState<string|null>(null);
  const [archivedAt, setArchivedAt] = useState<string|null>(null);
  const [finalising, setFinalising] = useState(false);
  const [mcDownloading, setMcDownloading] = useState(false);
  const [showFinaliseConfirm, setShowFinaliseConfirm] = useState(false);
  // Regenerate from the editor — creates a fresh version (all versions kept).
  const [regenerating, setRegenerating] = useState(false);
  const [showRegenConfirm, setShowRegenConfirm] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [canWrite, setCanWrite] = useState(true);
  // 2026-07-12: license tier, used to route the family-memories button (F&F
  // owners go to writer management via the invite page's tier redirect).
  const [licenseTier, setLicenseTier] = useState<string | null>(null);
  // Generation-cycle counter (Phase 2/3). null = grandfathered/unlimited or unknown.
  const [genCycles, setGenCycles] = useState<{ used: number; total: number; remaining: number } | null>(null);

  // Phase 6d — real-time co-author presence + save broadcast
  const [peerSaveToast, setPeerSaveToast] = useState<{ name: string; at: number } | null>(null);
  const presenceIdentity = user
    ? {
        userId: user.id,
        name: user.fullName || user.firstName || user.primaryEmailAddress?.emailAddress || "A co-author",
      }
    : null;
  const { peers, broadcastSave, setEditing, connected: presenceConnected } = useCoauthorPresence({
    channelKey: currentSessionId || null,
    me: presenceIdentity,
    onPeerSave: (evt) => setPeerSaveToast({ name: evt.name, at: evt.at }),
  });

  useEffect(() => {
    if (!peerSaveToast) return;
    // 8s — the toast now carries a "Show latest" action, give it time to be used.
    const t = window.setTimeout(() => setPeerSaveToast(null), 8000);
    return () => window.clearTimeout(t);
  }, [peerSaveToast]);

  // F3 (2026-07-05): collision awareness. Typing marks me as "editing now"
  // (surfaced on every co-author's presence strip); 20s of idle clears it.
  const editingIdleRef = useRef<number | null>(null);
  const markEditing = useCallback(() => {
    setEditing(true);
    if (editingIdleRef.current) window.clearTimeout(editingIdleRef.current);
    editingIdleRef.current = window.setTimeout(() => setEditing(false), 20000);
  }, [setEditing]);
  useEffect(() => () => { if (editingIdleRef.current) window.clearTimeout(editingIdleRef.current); }, []);

  // F3: one-click catch-up when a co-author saves. Cancels any pending
  // autosave FIRST (so my stale content can't clobber their fresh version),
  // then empties the editor and re-runs loadData — the populate effect only
  // fills an empty editor, so this is the sanctioned reload path (same
  // pattern as Regenerate's reload nonce).
  const showLatestFromPeer = useCallback(() => {
    clearTimeout((window as any)._cst);
    setPeerSaveToast(null);
    setEditing(false);
    if (edRef.current) edRef.current.innerHTML = "";
    setReloadNonce((n) => n + 1);
  }, [setEditing]);

  // Responsive check
  useEffect(() => {
    const ck = () => setIsMobile(window.innerWidth <= 840);
    ck(); window.addEventListener("resize", ck); return () => window.removeEventListener("resize", ck);
  }, []);

  // 2026-05-10: splitter drag handlers - wire document-level pointer events
  // while a drag is in progress. Touchmove uses passive:false so we can
  // preventDefault and stop the page scrolling under the user's finger.
  useEffect(() => {
    const computeWidth = (clientX: number) => {
      const w = window.innerWidth - clientX;
      return Math.max(SB_WIDTH_MIN, Math.min(SB_WIDTH_MAX, w));
    };
    const onMove = (e: MouseEvent) => {
      if (!sbDraggingRef.current) return;
      setSbWidth(computeWidth(e.clientX));
    };
    const onTouchMove = (e: TouchEvent) => {
      if (!sbDraggingRef.current || e.touches.length === 0) return;
      e.preventDefault();
      setSbWidth(computeWidth(e.touches[0].clientX));
    };
    const onUp = () => {
      if (!sbDraggingRef.current) return;
      sbDraggingRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onUp);
    window.addEventListener("touchcancel", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onUp);
      window.removeEventListener("touchcancel", onUp);
    };
  }, []);

  // Persist sidebar width to localStorage when it changes (desktop only).
  useEffect(() => {
    if (typeof window === "undefined" || isMobile) return;
    window.localStorage.setItem("cyrano_editor_sb_width", String(sbWidth));
  }, [sbWidth, isMobile]);

  // Track the id of the version being edited, for the leave-flush below.
  useEffect(() => {
    activeVersionIdRef.current = versions.find(v => v.active)?.id || null;
  }, [versions]);

  // Save-on-leave: a final, unconditional flush of the current editor content
  // when the page is hidden/closed (pagehide also covers refresh + bfcache) or
  // when the editor unmounts (SPA navigation away). keepalive lets the request
  // outlive the page; this is the backstop so nothing typed in the last instant
  // before leaving can be lost to the autosave debounce.
  useEffect(() => {
    const flush = () => {
      const ed = edRef.current;
      if (!ed || !tokenRef.current || !activeVersionIdRef.current) return;
      try {
        fetch("/api/eulogy/save", {
          method: "POST",
          keepalive: true,
          headers: { "Authorization": `Bearer ${tokenRef.current}`, "Content-Type": "application/json" },
          body: JSON.stringify({ eulogyId: activeVersionIdRef.current, content: ed.innerHTML, wordCount: cw(ed.innerHTML) }),
        }).catch(() => {});
      } catch { /* best-effort */ }
    };
    window.addEventListener("pagehide", flush);
    return () => { window.removeEventListener("pagehide", flush); flush(); };
  }, []);

  // Load eulogy data from database
  useEffect(() => {
    async function loadData() {
      setLoading(true);
      setError(null);
      try {
        const token = await getToken();
        if (!token) { setError("Not authenticated"); setLoading(false); return; }
        tokenRef.current = token;

        // If we have a eulogyId, load that specific eulogy content
        // If we have a sessionId, load the latest eulogy for that session
        const lookupId = eulogyId || sessionId;
        if (!lookupId) { setError("No eulogy or session ID provided"); setLoading(false); return; }

        const resp = await fetch(`/api/eulogy/load?${eulogyId ? `eulogyId=${eulogyId}` : `sessionId=${lookupId}`}`, {
          headers: { "Authorization": `Bearer ${token}` },
        });

        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          throw new Error(err.error || "Failed to load eulogy");
        }

        const data = await resp.json();

        // Set document type and title
        const type = data.conversation?.type || "eulogy";
        setDocType(type);
        const dn = data.conversation?.deceased_name || "your loved one";
        setDeceasedName(dn);
        setPreferredName(data.conversation?.preferred_name || "");
        setTitle(`${dn} — ${type === "tribute" ? "Tribute" : "Eulogy"}`);
        setCurrentSessionId(data.conversation?.id || sessionId);
        setTestamentLicenseId(data.conversation?.testament_license_id || null);
        // Pre-fill the export dialog's cover dates from what was captured in
        // onboarding, so they're not re-entered (the user can still edit them).
        const dob = data.conversation?.date_of_birth || "";
        const dop = data.conversation?.date_of_passing || "";
        setDatesOnFile({ birth: dob, passing: dop });
        if (dob || dop) {
          setExportFields(f => ({
            ...f,
            birthDate: f.birthDate || dob,
            deathDate: f.deathDate || dop,
          }));
        }
        setFinalisedAt(data.conversation?.finalised_at || null);
        setFinalVersionId(data.conversation?.final_version_id || null);
        setArchivedAt(data.conversation?.archived_at || null);
        // Who can write (owners + writers); read-only viewers can't regenerate.
        setCanWrite(data.access?.canWrite !== false);
        setLicenseTier(data.access?.tier || null);
        // Generation-cycle counter. null total = grandfathered/unlimited.
        const gTotal = data.conversation?.generation_cycles_total;
        const gUsed = data.conversation?.generation_cycles_used ?? 0;
        setGenCycles(gTotal == null ? null : { used: gUsed, total: gTotal, remaining: Math.max(0, gTotal - gUsed) });

        // Set versions
        const versionList: EulogyVersion[] = (data.versions || []).map((v: any, i: number) => ({
          id: v.id,
          label: i === 0 ? "Current draft" : v.style_variant ? `${v.style_variant} variant` : `Version ${v.version}`,
          time: formatTimeAgo(v.created_at),
          active: v.is_current,
          words: v.word_count || 0,
          content: v.content || "",
          style_variant: v.style_variant,
        }));
        setVersions(versionList);

        // Load current content into editor
        const current = versionList.find(v => v.active) || versionList[0];
        if (current && edRef.current) {
          const cleaned = cleanForEditor(current.content, dn);
          edRef.current.innerHTML = cleaned;
          setWc(cw(cleaned));
        }

        // Set contributors
        if (data.contributors && data.contributors.length > 0) {
          setContributors(data.contributors.map((c: any) => ({
            initials: getInitials(c.contributor_name),
            name: c.contributor_name,
          })));
        }

        // Build notes from conversation context
        const notesSections: NoteSection[] = [];
        if (data.memories && data.memories.length > 0) {
          notesSections.push({
            title: "Memories gathered",
            colour: B.gold,
            items: data.memories,
          });
        }
        if (data.themes && data.themes.length > 0) {
          notesSections.push({
            title: "Themes Cyrano found",
            colour: B.lavender,
            items: data.themes,
          });
        }
        if (data.contributorHighlights && data.contributorHighlights.length > 0) {
          notesSections.push({
            title: "Contributor highlights",
            colour: B.navy,
            items: data.contributorHighlights,
          });
        }
        setNotes(notesSections);
        setLoading(false);
      } catch (err: any) {
        console.error("Error loading eulogy:", err);
        setError(err.message || "Failed to load eulogy");
        setLoading(false);
      }
    }

    loadData();
  }, [eulogyId, sessionId, getToken, reloadNonce]);

  // Populate editor content once versions are loaded and editor div is mounted
  // This fixes a race condition: the editor div only renders when loading=false,
  // but the content was previously set before setLoading(false) when edRef was null
  useEffect(() => {
    if (loading || versions.length === 0 || !edRef.current) return;
    // Only set if editor is currently empty (avoid overwriting user edits)
    if (edRef.current.innerHTML && edRef.current.innerText.trim().length > 0) return;
    const current = versions.find(v => v.active) || versions[0];
    if (current) {
      const cleaned = cleanForEditor(current.content, deceasedName);
      edRef.current.innerHTML = cleaned;
      setWc(cw(cleaned));
    }
  }, [loading, versions]);

  // Fetch available photos when export dialog opens
  useEffect(() => {
    if (!showExportDialog || !testamentLicenseId) return;
    if (exportPhotos.length > 0) return; // already loaded
    setPhotosLoading(true);
    (async () => {
      try {
        const token = await getToken();
        if (!token) return;
        const resp = await fetch(`/api/photos/list?testamentLicenseId=${testamentLicenseId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (resp.ok) {
          const data = await resp.json();
          const photos = (data.photos || []).map((p: any) => ({
            id: p.id,
            url: p.thumbnail_url || p.public_url,
            caption: p.caption || null,
            isFeatured: p.is_featured || false,
          }));
          setExportPhotos(photos);
          // Cover stays on the Cyrano clouds by default; the user can pick a photo.
        }
      } catch (err) {
        console.warn("Failed to load photos for export:", err);
      } finally {
        setPhotosLoading(false);
      }
    })();
  }, [showExportDialog, testamentLicenseId, getToken, exportPhotos.length]);

  // Editor commands
  const exec = useCallback((c: string, v?: string) => {
    document.execCommand(c, false, v || undefined);
    edRef.current?.focus(); ua();
  }, []);

  const ua = useCallback(() => {
    const s: Record<string,boolean> = {};
    ["bold","italic","underline","insertUnorderedList","insertOrderedList","justifyLeft","justifyCenter"].forEach(c => { s[c] = document.queryCommandState(c); });
    setAct(s);
  }, []);

  // Save the current editor content to the active version. Used by the debounced
  // autosave (typing) and called immediately after programmatic edits (e.g.
  // inserting a quote) — those never fire onInput, so without an explicit save
  // the inserted content was silently lost on refresh.
  const persist = useCallback(async () => {
    const ed = edRef.current;
    if (!ed) return;
    try {
      const token = await getToken();
      if (token) tokenRef.current = token; // keep the leave-flush token fresh
      const activeVersion = versions.find(v => v.active);
      if (token && activeVersion) {
        const resp = await fetch("/api/eulogy/save", {
          method: "POST",
          headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({
            eulogyId: activeVersion.id,
            content: ed.innerHTML,
            wordCount: cw(ed.innerHTML),
          }),
        });
        if (resp.ok) broadcastSave();
      }
    } catch (e) {
      console.warn("Save failed:", e);
    } finally {
      setSave("saved");
    }
  }, [getToken, versions, broadcastSave]);

  const onInput = useCallback(() => {
    if (!edRef.current) return;
    setWc(cw(edRef.current.innerHTML));
    setSave("saving");
    markEditing(); // F3: co-authors see "editing now" while I type
    clearTimeout((window as any)._cst);
    (window as any)._cst = setTimeout(() => { void persist(); }, 1500);
  }, [persist, markEditing]);

  const onKey = useCallback((e: React.KeyboardEvent) => {
    if (e.key==="Tab") { e.preventDefault(); document.execCommand("insertHTML",false,"&nbsp;&nbsp;&nbsp;&nbsp;"); }
    if ((e.metaKey||e.ctrlKey)&&e.key==="b") { e.preventDefault(); exec("bold"); }
    if ((e.metaKey||e.ctrlKey)&&e.key==="i") { e.preventDefault(); exec("italic"); }
    if ((e.metaKey||e.ctrlKey)&&e.key==="u") { e.preventDefault(); exec("underline"); }
  }, [exec]);

  useEffect(() => { document.addEventListener("selectionchange", ua); return () => document.removeEventListener("selectionchange", ua); }, [ua]);

  const goToConversation = useCallback(() => {
    const params = new URLSearchParams();
    if (currentSessionId) params.set("sessionId", currentSessionId);
    params.set("type", docType);
    if (deceasedName) params.set("name", deceasedName);
    navigate(`/conversation?${params.toString()}`);
  }, [currentSessionId, docType, deceasedName, navigate]);

  // 2026-07-12 UX: one plainly named family door from the editor too — goes to
  // the same family-memories page as the Dashboard card (invite + shared
  // memories together), replacing the old Collaboration Dashboard route.
  const goToFamilyMemories = useCallback(() => {
    const params = new URLSearchParams({
      eulogy_id: currentSessionId || "",
      name: deceasedName || "",
      type: docType,
    });
    if (testamentLicenseId) params.set("licenseId", testamentLicenseId);
    if (licenseTier) params.set("tier", licenseTier);
    window.location.href = `/invite-collaborators.html?${params.toString()}`;
  }, [currentSessionId, deceasedName, docType, testamentLicenseId, licenseTier]);

  // Handle refinement generation
  const handleGen = useCallback(async (type: string, detail: string) => {
    setGenerating(true);
    setGenMsg(
      type==="tone" ? `Adjusting tone: ${detail}\u2026` :
      type==="style" ? `Channelling the style of ${detail}\u2026` :
      "Generating new version\u2026"
    );

    try {
      const token = await getToken();
      if (!token) throw new Error("Not authenticated");

      const currentContent = edRef.current?.innerHTML || "";

      const resp = await fetch("/api/eulogy/refine", {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionId: currentSessionId,
          currentContent,
          refinementType: type,
          refinementDetail: detail,
          docType,
        }),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || "Refinement failed");
      }

      const data = await resp.json();

      // Update editor with new content
      if (edRef.current && data.content) {
        edRef.current.innerHTML = data.content;
        setWc(cw(data.content));
      }

      // Add new version to list
      const nv: EulogyVersion = {
        id: data.eulogyId || `v${versions.length+1}`,
        label:
          type==="tone" ? `${detail} adjustment` :
          type==="style" ? `In the style of ${detail}` :
          "Custom revision",
        time: "Just now",
        active: true,
        words: data.wordCount || cw(data.content || ""),
        content: data.content || "",
        style_variant: type === "tone" ? detail : type === "style" ? `style:${detail}` : undefined,
      };
      setVersions([nv, ...versions.map(v => ({...v, active:false}))]);
      setSave("saved");
      setSelTone(null);
      setSelStyle("");
      setCustomReq("");
      setGenMsg(""); // clear the "Adjusting tone…" progress line once done (it was lingering into other panels)
    } catch (err: any) {
      console.error("Refinement error:", err);
      setGenMsg(`Error: ${err.message}`);
      setTimeout(() => setGenMsg(""), 3000);
    } finally {
      setGenerating(false);
    }
  }, [versions, getToken, currentSessionId, docType]);

  // Switch active version
  const switchVersion = useCallback((index: number) => {
    const v = versions[index];
    if (!v || !edRef.current) return;
    const cleaned = cleanForEditor(v.content, deceasedName);
    edRef.current.innerHTML = cleaned;
    setWc(cw(cleaned));
    setVersions(versions.map((vv, ii) => ({...vv, active: ii === index})));
    // Cancel any pending typing-save aimed at the old version so it can't race.
    clearTimeout((window as any)._cst);
    // Persist the choice so reverting STICKS across reloads (the DB's is_current
    // must follow the version the user is now viewing/editing). Only for real
    // saved versions (UUID id) — a local fallback id has nothing to point at.
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v.id)) {
      setSave("saving");
      (async () => {
        try {
          const token = await getToken();
          if (token) {
            tokenRef.current = token;
            await fetch("/api/eulogy/set-current", {
              method: "POST",
              headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
              body: JSON.stringify({ eulogyId: v.id }),
            });
          }
        } catch (e) { console.warn("Set-current failed:", e); }
        finally { setSave("saved"); }
      })();
    }
  }, [versions, deceasedName, getToken]);

  // Suggest quotes
  const handleSuggestQuotes = useCallback(async () => {
    setQuotesLoading(true);
    setQuotesError(null);
    setQuoteSuggestions([]);
    try {
      const token = await getToken();
      if (!token) throw new Error("Not authenticated");
      const content = edRef.current?.innerHTML || "";
      if (!content.trim()) { setQuotesError("Write some content first so Cyrano can match quotes to it."); return; }
      const resp = await fetch("/api/eulogy/suggest-quotes", {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ eulogyContent: content, deceasedName, preferredName: displayFirst, docType }),
      });
      if (!resp.ok) { const err = await resp.json().catch(() => ({})); throw new Error(err.error || "Failed to suggest quotes"); }
      const data = await resp.json();
      if (data.success && Array.isArray(data.suggestions)) { setQuoteSuggestions(data.suggestions); }
      else { setQuotesError("No suggestions returned. Try again."); }
    } catch (err: any) {
      console.error("Quote suggestion error:", err);
      setQuotesError(err.message || "Something went wrong");
    } finally {
      setQuotesLoading(false);
    }
  }, [getToken, deceasedName, displayFirst, docType]);

  // Insert the suggested quote where Cyrano recommends it belongs (per the
  // placement guidance), not wherever the cursor happens to be. Users are
  // typically non-technical, so it should land in the right spot automatically;
  // the Copy button is the "put it somewhere else myself" escape hatch.
  const insertQuoteAtPlacement = useCallback((q: { bridging: string; placement?: string; anchorText?: string }, index: number) => {
    const ed = edRef.current;
    if (!ed) return;

    const blockquote = document.createElement("blockquote");
    blockquote.style.borderLeft = `3px solid ${B.gold}`;
    blockquote.style.paddingLeft = "16px";
    blockquote.style.margin = "20px 0";
    blockquote.style.fontStyle = "italic";
    blockquote.style.color = B.navy;
    blockquote.style.fontFamily = "'Georgia', serif";
    blockquote.innerHTML = q.bridging;

    // Normalise text for matching: lowercase, straighten quotes, drop
    // punctuation, collapse whitespace — so a verbatim anchor phrase from the
    // model still matches the rendered paragraph despite formatting differences.
    const norm = (s: string) =>
      (s || "")
        .toLowerCase()
        .replace(/[‘’“”]/g, "'")
        .replace(/[^a-z0-9' ]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();

    const blocks = Array.from(ed.children) as HTMLElement[];
    const placement = (q.placement || "closing").toLowerCase();
    let inserted = false;

    if (placement === "opening" && ed.firstChild) {
      ed.insertBefore(blockquote, ed.firstChild);
      inserted = true;
    } else if (q.anchorText && (placement === "inline" || placement === "transition")) {
      // Find the paragraph that ends with (contains) the anchor phrase. Trim
      // leading words off the anchor and retry so a stray word never defeats
      // the match; require at least 4 words so it stays specific.
      let words = norm(q.anchorText).split(" ").filter(Boolean);
      let target: HTMLElement | undefined;
      while (!target && words.length >= 4) {
        const phrase = words.join(" ");
        target = blocks.find((b) => norm(b.textContent || "").includes(phrase));
        if (!target) words = words.slice(1);
      }
      if (target) {
        target.after(blockquote);
        inserted = true;
      }
    }

    // Default (closing) or anchor not found → place at the very end, never the top.
    if (!inserted) ed.appendChild(blockquote);

    // Show the user where it landed.
    blockquote.scrollIntoView({ behavior: "smooth", block: "center" });

    // Persist immediately — a programmatic DOM edit doesn't fire onInput, so
    // without this the inserted quote would be lost on refresh. Cancel any pending
    // debounced typing-save first so they don't race.
    setWc(cw(ed.innerHTML));
    setSave("saving");
    clearTimeout((window as any)._cst);
    void persist();
    setQuoteInserted(index);
    setTimeout(() => setQuoteInserted(null), 2000);
  }, [persist]);

  // Suggest poems
  const handleSuggestPoems = useCallback(async () => {
    setPoemsLoading(true);
    setPoemsError(null);
    setPoemSuggestions([]);
    try {
      const token = await getToken();
      if (!token) throw new Error("Not authenticated");
      const content = edRef.current?.innerHTML || "";
      if (!content.trim()) { setPoemsError("Write some content first so Cyrano can match poems to it."); setPoemsLoading(false); return; }
      const resp = await fetch("/api/eulogy/suggest-poems", {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ eulogyContent: content, deceasedName, preferredName: displayFirst, docType }),
      });
      if (!resp.ok) { const err = await resp.json().catch(() => ({})); throw new Error(err.error || "Failed to suggest poems"); }
      const data = await resp.json();
      if (data.success && Array.isArray(data.suggestions)) { setPoemSuggestions(data.suggestions); }
      else { setPoemsError("No suggestions returned. Try again."); }
    } catch (err: any) {
      console.error("Poem suggestion error:", err);
      setPoemsError(err.message || "Something went wrong");
    } finally {
      setPoemsLoading(false);
    }
  }, [getToken, deceasedName, displayFirst, docType]);

  // Print a poem as a clean single page for the reader at the service
  const printPoem = useCallback((poem: {title:string;author:string;fullText:string|null;excerpt:string;readingNote:string}) => {
    const poemText = poem.fullText
      ? poem.fullText.replace(/\\n/g, '\n').split('\n').map((l: string) => `<p style="margin:0 0 4px;line-height:1.9;font-size:14pt">${l || '&nbsp;'}</p>`).join('')
      : `<p style="font-style:italic;color:#3A3A3A;margin-bottom:16px;font-size:14pt;line-height:1.9">${poem.excerpt}</p><p style="color:#888880;font-size:11pt">This poem is under copyright. Please source the full text before the service.</p>`;
    const dn = deceasedName || 'your loved one';
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(`<!DOCTYPE html><html><head><title>${poem.title} — ${poem.author}</title>
<style>
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Inter:wght@400;500&display=swap');
* { margin:0; padding:0; box-sizing:border-box; }
@page { size: A4; margin: 0; }
body { font-family:'Georgia',serif; color:#3A3A3A; background:#fff; }

/* ---- HEADER STRIP ---- */
.header-strip {
  background:#1C2951;
  height:48px;
  position:relative;
}
.header-strip::after {
  content:'';
  display:block;
  height:4px;
  background:#d4adbb;
}
.header-brand {
  position:absolute;
  top:14px;
  right:28px;
  font-family:'Inter','Helvetica Neue',sans-serif;
  font-size:9pt;
  color:#d4adbb;
  letter-spacing:0.5px;
}

/* ---- CONTENT ---- */
.content {
  padding:48px 56px 40px;
  max-width:680px;
  margin:0 auto;
}

/* ---- EYEBROW ---- */
.eyebrow {
  font-family:'Inter','Helvetica Neue',sans-serif;
  font-size:9pt;
  font-weight:500;
  text-transform:uppercase;
  letter-spacing:2.5px;
  color:#d4adbb;
  text-align:center;
  margin-bottom:20px;
}

/* ---- GOLD RULE ---- */
.gold-rule {
  width:60px;
  height:1.5px;
  background:#d4adbb;
  margin:0 auto 24px;
}
.gold-rule-wide {
  width:100%;
  height:1px;
  background:#d4adbb;
  margin:28px 0;
  opacity:0.5;
}

/* ---- POEM TITLE ---- */
.poem-title {
  font-family:'Playfair Display','Georgia',serif;
  font-size:26pt;
  font-weight:600;
  color:#1C2951;
  text-align:center;
  margin-bottom:6px;
  line-height:1.3;
}
.poem-author {
  font-family:'Georgia',serif;
  font-size:13pt;
  color:#888880;
  text-align:center;
  font-style:italic;
  margin-bottom:32px;
}

/* ---- POEM BODY ---- */
.poem-body {
  text-align:center;
  margin-bottom:8px;
  color:#3A3A3A;
  font-family:'Georgia',serif;
}

/* ---- READING NOTE ---- */
.reading-note {
  font-size:10pt;
  color:#888880;
  font-style:italic;
  text-align:center;
  padding:16px 24px;
  line-height:1.6;
}
.reading-note strong {
  font-style:normal;
  color:#1C2951;
  font-weight:600;
}

/* ---- MEMORIAL LINE ---- */
.memorial {
  font-family:'Playfair Display','Georgia',serif;
  font-size:11pt;
  color:#1C2951;
  text-align:center;
  font-style:italic;
  margin-top:4px;
}

/* ---- FOOTER ---- */
.footer-strip {
  position:fixed;
  bottom:0;
  left:0;
  right:0;
}
.footer-gold {
  height:1px;
  background:#d4adbb;
  opacity:0.4;
}
.footer-navy {
  background:#1C2951;
  height:36px;
  display:flex;
  align-items:center;
  justify-content:center;
}
.footer-text {
  font-family:'Inter','Helvetica Neue',sans-serif;
  font-size:8pt;
  color:#d4adbb;
  letter-spacing:1px;
}

@media (max-width: 480px) {
  .content { padding:32px 24px 28px; }
  .poem-title { font-size:20pt; }
  .poem-author { font-size:12pt; }
  .header-brand { right:16px; font-size:8pt; }
}
@media print {
  body { -webkit-print-color-adjust:exact; print-color-adjust:exact; }
  .header-strip, .footer-strip { position:fixed; left:0; right:0; }
  .header-strip { top:0; }
  .footer-strip { bottom:0; }
  .content { padding:48px 56px 40px; }
}
</style></head><body>

<!-- Header -->
<div class="header-strip">
  <span class="header-brand">Cyrano &middot; cyrano.cloud</span>
</div>

<!-- Content -->
<div class="content">
  <div class="eyebrow">A Reading for the Service</div>
  <div class="gold-rule"></div>

  <div class="poem-title">${poem.title}</div>
  <div class="poem-author">${poem.author}</div>

  <div class="poem-body">${poemText}</div>

  <div class="gold-rule-wide"></div>

  ${poem.readingNote ? `<div class="reading-note"><strong>Reading note:</strong> ${poem.readingNote}</div>` : ''}

  <div class="gold-rule-wide"></div>

  <div class="memorial">In loving memory of ${dn}</div>
</div>

<!-- Footer -->
<div class="footer-strip">
  <div class="footer-gold"></div>
  <div class="footer-navy">
    <span class="footer-text">Prepared with Cyrano &middot; cyrano.cloud</span>
  </div>
</div>

</body></html>`);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 300);
  }, []);

  // Copy poem text for sharing (e.g. email to the reader)
  const copyPoemForSharing = useCallback((poem: {title:string;author:string;fullText:string|null;excerpt:string;connection:string;readingNote:string}, index: number) => {
    const poemText = poem.fullText ? poem.fullText.replace(/\\n/g, '\n') : `"${poem.excerpt}"\n\n(This poem is under copyright — please search for the full text online.)`;
    const shareText = `${poem.title}\nby ${poem.author}\n\n${poemText}\n\n---\nReading note: ${poem.readingNote}`;
    navigator.clipboard.writeText(shareText);
    setPoemCopied(index);
    setTimeout(() => setPoemCopied(null), 2000);
  }, []);

  // Export
  const handleExportFormat = useCallback(async (format: string) => {
    const activeVersion = versions.find(v => v.active) || versions[0];
    if (!activeVersion) return;
    setExportLoading(format);
    try {
      const token = await getToken();
      if (!token) return;

      let endpoint = "";
      const isSocial = format === "facebook" || format === "instagram" || format === "instagram-story";
      if (format === "docx" || format === "gdocs") endpoint = "/api/eulogy/export-docx"; // Google Docs opens a .docx
      else if (format === "pptx") endpoint = "/api/eulogy/export-pptx";
      else if (format === "pdf" || format === "speaker") endpoint = "/api/eulogy/export-pdf"; // speaker = large-print PDF
      else if (isSocial) endpoint = "/api/eulogy/export-social";
      else if (format === "txt") {
        // Plain text — client-side
        if (!edRef.current) return;
        const text = edRef.current.innerText;
        const blob = new Blob([text], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${title.replace(/[^a-zA-Z0-9]/g, "_")}.txt`;
        a.click();
        URL.revokeObjectURL(url);
        setExportLoading(null);
        return;
      }

      const resp = await fetch(endpoint, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          eulogyId: activeVersion.id,
          ...exportFields,
          ...(isSocial ? { format } : {}),
          ...(format === "speaker" ? { speakerCopy: true } : {}),
          ...(coverPhotoId ? { coverPhotoId } : {}),
        }),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || "Export failed");
      }

      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ext = isSocial ? "png" : format === "gdocs" ? "docx" : format === "speaker" ? "pdf" : format;
      const suffix = format === "speaker" ? "_Speakers_Copy" : "";
      a.download = `${title.replace(/[^a-zA-Z0-9]/g, "_")}${suffix}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error("Export error:", err);
      setGenMsg(err.message || "Export failed");
      setTimeout(() => setGenMsg(""), 4000);
    } finally {
      setExportLoading(null);
    }
  }, [versions, title, getToken, exportFields, coverPhotoId]);

  // Regenerate a fresh version from the editor. Calls /api/eulogy/generate with
  // regenerate:true — the server consumes one generation cycle, enforces the cap
  // (409 'generations_exhausted' → top-up), and saves the result as a NEW version
  // (the prior version is kept, just no longer current). We then reload to show
  // the new draft and refresh the Versions list + remaining count.
  const handleRegenerate = useCallback(async () => {
    if (!currentSessionId || !user?.id || regenerating) return;
    setRegenerating(true);
    setGenMsg("Creating a fresh version…");
    try {
      const token = await getToken();
      const active = versions.find(v => v.active) || versions[0];
      const resp = await fetch("/api/eulogy/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          userId: user.id,
          sessionId: currentSessionId,
          regenerate: true,
          styleVariant: active?.style_variant || "warm",
          targetDuration: 5,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.status === 409 && data.code === "generations_exhausted") {
        // Stay on the dialog — it switches to the top-up prompt (remaining 0).
        setGenCycles({ used: data.cycles_used ?? 0, total: data.cycles_total ?? 0, remaining: 0 });
        setGenMsg("");
        return;
      }
      if (!resp.ok || !data.success) throw new Error(data.error || "Could not create a new version");
      if (typeof data.cycles_total === "number") {
        setGenCycles({ used: data.cycles_used ?? 0, total: data.cycles_total, remaining: data.cycles_remaining ?? 0 });
      }
      setShowRegenConfirm(false);
      setReloadNonce(n => n + 1); // re-loads versions + drops the new draft into the editor
      setGenMsg("New version created ✓");
      setTimeout(() => setGenMsg(""), 3000);
    } catch (err: any) {
      setShowRegenConfirm(false);
      setGenMsg(err.message || "Could not regenerate");
      setTimeout(() => setGenMsg(""), 4500);
    } finally {
      setRegenerating(false);
    }
  }, [currentSessionId, user, getToken, versions, regenerating]);

  // Top-up generation cycles ($9.95 / 15) when the user has run out.
  const handleTopup = useCallback(async () => {
    if (!testamentLicenseId) return;
    try {
      const token = await getToken();
      const resp = await fetch("/api/stripe/create-topup-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ licenseId: testamentLicenseId, conversationId: currentSessionId }),
      });
      const data = await resp.json().catch(() => ({}));
      if (data.checkoutUrl) { window.location.href = data.checkoutUrl; return; }
      throw new Error(data.error || "Could not start top-up");
    } catch (err: any) {
      setGenMsg(err.message || "Could not start top-up");
      setTimeout(() => setGenMsg(""), 4500);
    }
  }, [testamentLicenseId, currentSessionId, getToken]);

  // Phase 5 — finalise, un-finalise, Memory Collection download
  const handleFinalise = useCallback(async () => {
    if (!testamentLicenseId || !currentSessionId) {
      setGenMsg("Cannot finalise — missing license or conversation");
      setTimeout(()=>setGenMsg(""), 4000);
      return;
    }
    setFinalising(true);
    setGenMsg("Finalising…");
    try {
      const token = await getToken();
      if (!token) throw new Error("Not authenticated");
      const resp = await fetch("/api/eulogy/finalise", {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ conversationId: currentSessionId, licenseId: testamentLicenseId }),
      });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok || !j.success) {
        if (j.error === "license_archived") throw new Error(j.message || "This piece was archived and cannot be finalised.");
        if (j.error === "no_current_version") throw new Error(j.message || "Generate the piece first.");
        if (j.error === "conversation_mismatch") throw new Error(j.message || "Conversation does not match this license.");
        throw new Error(j.error || "Finalisation failed");
      }
      setFinalisedAt(j.finalisedAt || new Date().toISOString());
      setFinalVersionId(j.finalVersionId || null);
      setShowFinaliseConfirm(false);
      setGenMsg(j.alreadyFinalised ? "Already finalised" : "Finalised \u2713");
      setTimeout(()=>setGenMsg(""), 3000);
    } catch (err: any) {
      console.error("Finalise error:", err);
      setGenMsg(err.message || "Finalisation failed");
      setTimeout(()=>setGenMsg(""), 5000);
    } finally {
      setFinalising(false);
    }
  }, [testamentLicenseId, currentSessionId, getToken]);

  const handleUnfinalise = useCallback(async () => {
    if (!testamentLicenseId) return;
    setFinalising(true);
    setGenMsg("Un-finalising…");
    try {
      const token = await getToken();
      if (!token) throw new Error("Not authenticated");
      const resp = await fetch("/api/eulogy/unfinalise", {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ licenseId: testamentLicenseId }),
      });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok || !j.success) throw new Error(j.error || "Un-finalise failed");
      setFinalisedAt(null);
      setFinalVersionId(null);
      setGenMsg("Un-finalised \u2014 you can Generate / Refine again");
      setTimeout(()=>setGenMsg(""), 3500);
    } catch (err: any) {
      console.error("Un-finalise error:", err);
      setGenMsg(err.message || "Un-finalise failed");
      setTimeout(()=>setGenMsg(""), 5000);
    } finally {
      setFinalising(false);
    }
  }, [testamentLicenseId, getToken]);

  const handleDownloadMemoryCollection = useCallback(async () => {
    if (!testamentLicenseId) return;
    setMcDownloading(true);
    try {
      const token = await getToken();
      if (!token) throw new Error("Not authenticated");
      const resp = await fetch("/api/eulogy/export-memory-collection", {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ licenseId: testamentLicenseId, ...exportFields }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        if (err.error === "not_finalised") throw new Error(err.message || "Finalise the piece first.");
        throw new Error(err.error || "Download failed");
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(deceasedName || "Memory_Collection").replace(/[^a-zA-Z0-9]/g, "_")}_Memory_Collection.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error("Memory Collection download error:", err);
      setGenMsg(err.message || "Download failed");
      setTimeout(()=>setGenMsg(""), 5000);
    } finally {
      setMcDownloading(false);
    }
  }, [testamentLicenseId, getToken, exportFields, deceasedName]);

  // Target word counts
  const tW = docType==="eulogy" ? [800,1000] : [400,500];
  const tT = docType==="eulogy" ? "5\u20137 min" : "~3 min";
  const inR = wc>=tW[0]&&wc<=tW[1], ov = wc>tW[1];

  // Loading state
  if (loading) {
    return (
      <div style={{ minHeight:"100vh", background:B.cream, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Inter',system-ui,sans-serif" }}>
        <div style={{ textAlign:"center" }}>
          <img src="/Assets/GhostQuillLogo.png" alt="Cyrano" style={{ width:48, height:48, marginBottom:16, animation:"pulse 2s infinite" }} />
          <p style={{ color:B.wg, fontSize:15 }}>Loading your {docType}...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{ minHeight:"100vh", background:B.cream, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Inter',system-ui,sans-serif" }}>
        <div style={{ background:B.w, borderRadius:16, padding:40, textAlign:"center", maxWidth:400, boxShadow:"0 4px 24px rgba(25,40,92,0.08)" }}>
          <img src="/Assets/200-handdrawn-icons-by-heloholo/png/48/warning48.png" alt="" style={{ width:48, height:48, marginBottom:16, filter:"brightness(0) saturate(100%) invert(15%) sepia(30%) saturate(2000%) hue-rotate(200deg) brightness(90%) contrast(95%)" }} />
          <h2 style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:20, color:B.navy, marginBottom:8 }}>Something went wrong</h2>
          <p style={{ color:B.wg, marginBottom:20, fontSize:14 }}>{error}</p>
          <button onClick={() => navigate("/dashboard")} style={{ padding:"10px 24px", background:B.navy, color:B.w, border:"none", borderRadius:8, fontSize:14, cursor:"pointer" }}>Back to Dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily:"'Inter',system-ui,-apple-system,sans-serif", minHeight:"100vh", background:B.w, display:"flex", flexDirection:"column" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=Inter:wght@400;500;600&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
        html,body{overflow-x:hidden;max-width:100%}
        .ce-s{font-family:'Georgia',serif;font-size:17px;line-height:1.9;color:${B.tx};min-height:600px;outline:none;padding:48px 56px;-webkit-font-smoothing:antialiased}
        .ce-s p{margin-bottom:1em}
        .ce-s h2{font-family:'Playfair Display',Georgia,serif;font-size:24px;font-weight:600;color:${B.navy};margin:1.4em 0 .5em;line-height:1.3}
        .ce-s blockquote{border-left:3px solid ${B.gold};padding:12px 0 12px 24px;margin:1.2em 0;font-style:italic;color:${B.txM};background:${B.goldPale}40;border-radius:0 8px 8px 0}
        .ce-s ul,.ce-s ol{padding-left:28px;margin-bottom:1em}
        .ce-s li{margin-bottom:.4em}
        .ce-s ::selection{background:${B.lavender}50}
        @media(max-width:840px){.ce-s{padding:20px 16px;font-size:16px;line-height:1.75}.ce-s h2{font-size:20px}.ce-s blockquote{padding:10px 0 10px 16px;margin:1em 0}}
        @media(max-width:480px){.ce-s{padding:16px 14px;font-size:15px;line-height:1.7}.ce-s h2{font-size:18px}}
        .tb{width:34px;height:34px;border:none;border-radius:6px;background:transparent;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;font-family:'Georgia',serif;font-size:15px;transition:all .15s;flex-shrink:0;color:${B.txL}}
        .tb:hover{background:${B.wgF};color:${B.tx}}
        @media(max-width:840px){.tb{width:44px;height:44px;font-size:17px;border-radius:8px}}
        @keyframes gp{0%,100%{opacity:.5}50%{opacity:1}}
        .gp{animation:gp 1.2s ease-in-out infinite}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:.5}}
        textarea:focus{border-color:${B.gold}!important;outline:none}
        @media print{.no-print{display:none!important}}
        @keyframes slideUp{from{transform:translateY(100%)}to{transform:translateY(0)}}
        .bottom-sheet{animation:slideUp .25s ease-out}
      `}</style>

      {/* generating overlay */}
      {generating&&<div style={{position:"fixed",inset:0,zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(28,41,81,0.07)",backdropFilter:"blur(2px)"}}>
        <div style={{background:B.w,borderRadius:16,padding:"32px 40px",textAlign:"center",boxShadow:"0 8px 40px rgba(28,41,81,0.12)",maxWidth:340}}>
          <div className="gp" style={{width:48,height:48,borderRadius:"50%",background:B.goldPale,margin:"0 auto 16px",display:"flex",alignItems:"center",justifyContent:"center"}}><div style={{width:20,height:20,borderRadius:"50%",background:B.gold}}/></div>
          <div style={{fontSize:15,fontWeight:500,color:B.navy,marginBottom:6}}>{genMsg}</div>
          <div style={{fontSize:13,color:B.wg}}>This may take a moment</div>
        </div>
      </div>}

      {/* mobile sidebar overlay */}
      {isMobile&&mobileSb&&<div onClick={()=>setMobileSb(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.35)",zIndex:90}}/>}

      <div style={{display:"flex",flex:1,width:"100%",background:B.w,overflow:"hidden",position:"relative",minHeight:"100vh"}}>

        {/* MAIN COLUMN */}
        <div style={{flex:1,display:"flex",flexDirection:"column",minWidth:0}}>

          {/* toolbar */}
          <div className="no-print" style={{background:B.w,borderBottom:`1px solid ${B.wgP}40`,position:"sticky",top:0,zIndex:10}}>
            <div style={{padding:isMobile?"8px 10px 4px":"10px 20px 6px",display:"flex",alignItems:"center",gap:isMobile?6:10,flexWrap:isMobile?"wrap":"nowrap"}}>
              {!isMobile&&<div style={{display:"inline-flex",alignItems:"center",gap:5,padding:"3px 0",fontSize:11,fontWeight:600,letterSpacing:.6,textTransform:"uppercase",color:B.txM,flexShrink:0}}>
                <span style={{width:5,height:5,borderRadius:"50%",background:docType==="eulogy"?B.navy:B.lavender}}/>{docType}
              </div>}
              <input value={title} onChange={e=>setTitle(e.target.value)} style={{flex:1,minWidth:0,border:"none",outline:"none",fontSize:isMobile?14:17,fontFamily:"'Playfair Display',Georgia,serif",color:B.navy,fontWeight:600,background:"transparent",padding:"3px 0",...(isMobile?{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" as const}:{})}}/>
              <div style={{display:"flex",gap:6,flexShrink:0}}>
                {!isMobile&&<button onClick={goToConversation} style={{border:`1px solid ${B.wgP}60`,background:"transparent",color:B.txL,borderRadius:8,padding:"5px 14px",fontSize:12,fontFamily:"'Inter',system-ui,sans-serif",cursor:"pointer",fontWeight:500,transition:"all .2s",display:"inline-flex",alignItems:"center",gap:5}}>
                  <img src="/Assets/200-handdrawn-icons-by-heloholo/png/48/comment148.png" alt="" style={{width:14,height:14,opacity:.6}}/> Continue Conversation
                </button>}
                {!isMobile&&currentSessionId&&canWrite&&!finalisedAt&&!archivedAt&&<button onClick={()=>setShowRegenConfirm(true)} disabled={regenerating} title="Generate a fresh version (your current draft is kept in Versions)" style={{border:`1px solid ${B.wgP}60`,background:"transparent",color:B.txL,borderRadius:8,padding:"5px 14px",fontSize:12,fontFamily:"'Inter',system-ui,sans-serif",cursor:regenerating?"wait":"pointer",fontWeight:500,transition:"all .2s",display:"inline-flex",alignItems:"center",gap:5,opacity:regenerating?.6:1}}>
                  <img src="/Assets/200-handdrawn-icons-by-heloholo/png/48/reload_cw48.png" alt="" style={{width:14,height:14,opacity:.6}}/> {regenerating?"Regenerating…":"Regenerate"}
                </button>}
                {!isMobile&&currentSessionId&&<button onClick={goToFamilyMemories} style={{border:`1px solid ${B.wgP}60`,background:"transparent",color:B.txL,borderRadius:8,padding:"5px 14px",fontSize:12,fontFamily:"'Inter',system-ui,sans-serif",cursor:"pointer",fontWeight:500,transition:"all .2s",display:"inline-flex",alignItems:"center",gap:5}}>
                  <img src="/Assets/200-handdrawn-icons-by-heloholo/png/48/add-contact48.png" alt="" style={{width:14,height:14,opacity:.6}}/> Family &amp; friends
                </button>}
                {!isMobile&&<button onClick={()=>setFocus(!focus)} style={{border:`1px solid ${focus?B.gold:B.wgP}60`,background:focus?B.goldFaint:"transparent",color:focus?B.navy:B.txL,borderRadius:8,padding:"5px 14px",fontSize:12,fontFamily:"'Inter',system-ui,sans-serif",cursor:"pointer",fontWeight:500,transition:"all .2s"}}>
                  {focus?"Exit focus":"Focus"}
                </button>}
                {isMobile&&<>
                  <button onClick={goToConversation} style={{border:`1px solid ${B.wgP}60`,background:"transparent",color:B.txL,borderRadius:8,padding:"5px 10px",fontSize:12,fontFamily:"'Inter',system-ui,sans-serif",cursor:"pointer",fontWeight:500,minHeight:36,display:"inline-flex",alignItems:"center",gap:4}}>
                    <img src="/Assets/200-handdrawn-icons-by-heloholo/png/48/comment148.png" alt="" style={{width:12,height:12,opacity:.6}}/> Converse
                  </button>
                  {currentSessionId&&canWrite&&!finalisedAt&&!archivedAt&&<button onClick={()=>setShowRegenConfirm(true)} disabled={regenerating} style={{border:`1px solid ${B.wgP}60`,background:"transparent",color:B.txL,borderRadius:8,padding:"5px 10px",fontSize:12,fontFamily:"'Inter',system-ui,sans-serif",cursor:regenerating?"wait":"pointer",fontWeight:500,minHeight:36,display:"inline-flex",alignItems:"center",gap:4,opacity:regenerating?.6:1}}>
                    <img src="/Assets/200-handdrawn-icons-by-heloholo/png/48/reload_cw48.png" alt="" style={{width:12,height:12,opacity:.6}}/> {regenerating?"…":"Regen"}
                  </button>}
                  {currentSessionId&&<button onClick={()=>navigate(`/collaboration/${currentSessionId}`)} style={{border:`1px solid ${B.wgP}60`,background:"transparent",color:B.txL,borderRadius:8,padding:"5px 10px",fontSize:12,fontFamily:"'Inter',system-ui,sans-serif",cursor:"pointer",fontWeight:500,minHeight:36,display:"inline-flex",alignItems:"center",gap:4}}>
                    <img src="/Assets/200-handdrawn-icons-by-heloholo/png/48/add-contact48.png" alt="" style={{width:12,height:12,opacity:.6}}/> Collab
                  </button>}
                  <button onClick={()=>setFocus(!focus)} style={{border:`1px solid ${focus?B.gold:B.wgP}60`,background:focus?B.goldFaint:"transparent",color:focus?B.navy:B.txL,borderRadius:8,padding:"5px 10px",fontSize:12,fontFamily:"'Inter',system-ui,sans-serif",cursor:"pointer",fontWeight:500,minHeight:36}}>{focus?"Exit":"Focus"}</button>
                </>}
              </div>
            </div>
            {/* formatting toolbar */}
            {isMobile ? (
              <div style={{padding:"2px 8px 6px",opacity:focus?.12:1,transition:"opacity .3s"}}>
                <div style={{display:"flex",alignItems:"center",flexWrap:"nowrap",overflow:"hidden",gap:1}}>
                  {/* Primary buttons: Bold, Italic, Heading, Undo, Redo */}
                  {[
                    TB[0].items[0], // Bold
                    TB[0].items[1], // Italic
                    TB[1].items[0], // Heading
                    TB[4].items[0], // Undo
                    TB[4].items[1], // Redo
                  ].map(it=><button key={it.c+(it.v||"")} className="tb" title={it.t} onMouseDown={e=>{e.preventDefault();exec(it.c,it.v)}} style={{background:act[it.c]?B.goldPale:undefined,color:act[it.c]?B.navy:B.txM,...(it.s||{})}}>{it.ic}</button>)}
                  <div style={{width:1,height:18,background:B.wgP,margin:"0 4px",flexShrink:0}}/>
                  <button className="tb" title={mobileToolbarExpanded?"Less":"More"} onClick={()=>setMobileToolbarExpanded(!mobileToolbarExpanded)} style={{color:mobileToolbarExpanded?B.navy:B.txM,background:mobileToolbarExpanded?B.goldPale:undefined,fontSize:17}}>{mobileToolbarExpanded?"\u2715":"\u22EF"}</button>
                </div>
                {mobileToolbarExpanded&&<div style={{display:"flex",alignItems:"center",flexWrap:"nowrap",overflow:"hidden",gap:1,marginTop:2}}>
                  {/* Overflow buttons: Underline, Quote, Paragraph, Bullet, Number, Left, Centre */}
                  {[
                    TB[0].items[2], // Underline
                    TB[1].items[1], // Quote
                    TB[1].items[2], // Paragraph
                    TB[2].items[0], // Bullet list
                    TB[2].items[1], // Number list
                    TB[3].items[0], // Left
                    TB[3].items[1], // Centre
                  ].map(it=><button key={it.c+(it.v||"")} className="tb" title={it.t} onMouseDown={e=>{e.preventDefault();exec(it.c,it.v)}} style={{background:act[it.c]?B.goldPale:undefined,color:act[it.c]?B.navy:B.txM,...(it.s||{})}}>{it.ic}</button>)}
                </div>}
              </div>
            ) : (
              <div style={{padding:"2px 16px 8px",display:"flex",alignItems:"center",gap:1,flexWrap:"wrap",opacity:focus?.12:1,transition:"opacity .3s"}}>
                {TB.map((g,gi)=><div key={gi} style={{display:"inline-flex",alignItems:"center"}}>
                  {g.items.map(it=><button key={it.c+(it.v||"")} className="tb" title={it.t} onMouseDown={e=>{e.preventDefault();exec(it.c,it.v)}} style={{background:act[it.c]?B.goldPale:undefined,color:act[it.c]?B.navy:B.txM,...(it.s||{})}}>{it.ic}</button>)}
                  {gi<TB.length-1&&<div style={{width:1,height:18,background:B.wgP,margin:"0 5px",flexShrink:0}}/>}
                </div>)}
              </div>
            )}
          </div>

          {/* Finalised banner (Phase 5) */}
          {finalisedAt&&!focus&&<div className="no-print" style={{padding:isMobile?"10px 14px":"12px 22px",background:B.goldFaint,borderBottom:`1px solid ${B.gold}55`,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
            <span style={{fontSize:15,lineHeight:1,color:B.navy}}>{"\u2713"}</span>
            <div style={{flex:"1 1 auto",minWidth:200,fontSize:13,color:B.navy,lineHeight:1.5,fontFamily:"'Inter',system-ui,sans-serif"}}>
              <strong style={{fontWeight:600}}>Finalised</strong>
              <span style={{color:B.txM}}> {"\u00B7"} {new Date(finalisedAt).toLocaleDateString(undefined,{year:"numeric",month:"long",day:"numeric"})} {"\u00B7"} You can still make small edits here. Un-finalise to regenerate or refine.</span>
            </div>
            <button onClick={handleUnfinalise} disabled={finalising} style={{background:"transparent",border:"none",color:B.navy,fontSize:13,fontWeight:500,textDecoration:"underline",cursor:finalising?"wait":"pointer",padding:"4px 8px",fontFamily:"'Inter',system-ui,sans-serif",opacity:finalising?.5:1}}>
              {finalising?"Working\u2026":"Un-finalise"}
            </button>
          </div>}

          {/* collab + save bar */}
          {!focus&&<div className="no-print" style={{padding:isMobile?"6px 12px":"8px 20px",display:"flex",alignItems:"center",borderBottom:`1px solid ${B.wgP}20`}}>
            {contributors.length>0&&<>
              {contributors.map((c,i)=><div key={i} style={{width:26,height:26,borderRadius:"50%",background:[B.navy,B.lavender,B.gold][i%3],color:i===0?B.w:B.navy,display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:600,fontFamily:"'Inter',system-ui,sans-serif",marginLeft:i>0?-4:0,border:`2px solid ${B.w}`,position:"relative",zIndex:contributors.length-i}}>{c.initials}</div>)}
              <span style={{marginLeft:10,fontSize:12,color:B.wg}}>{contributors.length} contributor{contributors.length!==1?"s":""}</span>
            </>}
            <div style={{flex:1}}/>
            <CoauthorPresenceStrip peers={peers} connected={presenceConnected} compact={isMobile} />
            <div style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:save==="saved"?B.ok:B.wgL,marginLeft:peers.length>0?10:0}}>
              <span style={{width:6,height:6,borderRadius:"50%",background:save==="saved"?B.ok:save==="saving"?B.gold:B.wgL,transition:"background .3s"}}/>
              {save==="saved"?"All changes saved":save==="saving"?"Saving\u2026":"Draft"}
            </div>
          </div>}

          {/* Phase 6d peer-save toast */}
          {peerSaveToast&&<div className="no-print" style={{position:"fixed",bottom:isMobile?104:24,right:isMobile?12:24,zIndex:9999,background:B.navy,color:B.w,padding:"12px 16px",borderRadius:10,fontSize:13,fontFamily:"'Inter',system-ui,sans-serif",boxShadow:"0 8px 24px rgba(28,41,81,0.25)",display:"flex",alignItems:"center",gap:10,maxWidth:360,flexWrap:"wrap"}}>
            <span style={{width:8,height:8,borderRadius:"50%",background:B.gold,flexShrink:0}}/>
            <span style={{flex:1,minWidth:140}}>{peerSaveToast.name} just saved changes to this piece.</span>
            <button onClick={showLatestFromPeer} style={{background:B.w,color:B.navy,border:"none",borderRadius:8,padding:"8px 14px",minHeight:36,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',system-ui,sans-serif"}}>Show latest</button>
            <button onClick={()=>setPeerSaveToast(null)} aria-label="Dismiss" style={{background:"transparent",color:B.w,border:"none",fontSize:16,cursor:"pointer",padding:"6px 8px",minHeight:36,lineHeight:1}}>{"×"}</button>
          </div>}

          {/* editor surface — clean notebook page */}
          <div style={{flex:1,overflow:"auto",background:B.w,transition:"background .4s",...(isMobile?{paddingBottom:96}:{})}}>
            <div style={{...(isMobile?{background:B.w,minHeight:"calc(100vh - 160px)"}:{maxWidth:780,margin:"0 auto",background:B.w,minHeight:"calc(100vh - 200px)"})}}>
              <div ref={edRef} className="ce-s" contentEditable suppressContentEditableWarning spellCheck onInput={onInput} onKeyDown={onKey} onKeyUp={ua} onClick={ua} style={isMobile?{minHeight:"calc(100vh - 180px)"}:undefined} />
            </div>
          </div>

          {/* status bar — desktop only (mobile gets bottom action bar instead) */}
          {!isMobile&&<div className="no-print" style={{background:B.w,borderTop:`1px solid ${B.wgP}30`,padding:"9px 20px",display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:6,fontSize:12,color:B.wg}}>
            <div style={{display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
              <span><strong style={{color:B.navy,fontWeight:600}}>{wc.toLocaleString()}</strong> words</span>
              <span style={{width:1,height:12,background:B.wgP}}/>
              <span><strong style={{color:B.navy,fontWeight:600}}>{ft(wc)}</strong> speaking</span>
              <span style={{width:1,height:12,background:B.wgP}}/>
              <span style={{color:inR?B.ok:ov?B.err:B.wg,fontWeight:inR||ov?500:400}}>Target: {tW[0]}\u2013{tW[1]} words ({tT}){inR&&" \u2713"}</span>
            </div>
          </div>}
        </div>

        {/* SPLITTER (desktop only) - 2026-05-10 - drag to resize sidebar */}
        {!isMobile&&<div
          onMouseDown={e=>{e.preventDefault();sbDraggingRef.current=true;document.body.style.cursor="col-resize";document.body.style.userSelect="none";}}
          onTouchStart={()=>{sbDraggingRef.current=true;document.body.style.userSelect="none";}}
          onDoubleClick={()=>setSbWidth(SB_WIDTH_DEFAULT)}
          title="Drag to resize sidebar (double-click to reset)"
          style={{
            width:6,flexShrink:0,cursor:"col-resize",position:"relative",
            background:"transparent",transition:"background .15s",touchAction:"none",
            zIndex:5,
          }}
          onMouseEnter={e=>{e.currentTarget.style.background=B.wgP+"60";}}
          onMouseLeave={e=>{if(!sbDraggingRef.current)e.currentTarget.style.background="transparent";}}
        >
          {/* visual rail in the centre */}
          <div style={{position:"absolute",left:"50%",top:0,bottom:0,width:1,transform:"translateX(-50%)",background:B.wgP+"40",pointerEvents:"none"}}/>
        </div>}

        {/* SIDEBAR */}
        {(!isMobile||mobileSb)&&<div className={isMobile?"no-print bottom-sheet":"no-print"} style={{...(isMobile?{position:"fixed",left:0,right:0,bottom:0,zIndex:100,maxHeight:"65vh",borderRadius:"16px 16px 0 0",boxShadow:"0 -4px 24px rgba(0,0,0,0.12)",overscrollBehavior:"contain"}:{width:sbWidth,borderLeft:`1px solid ${B.wgP}40`,flexShrink:0}),background:B.cream,display:"flex",flexDirection:"column",overflow:"hidden"}}>

          {isMobile&&<div style={{padding:"10px 16px 0",display:"flex",flexDirection:"column",alignItems:"center"}}>
            {/* drag handle */}
            <div style={{width:36,height:4,borderRadius:2,background:B.wgP,marginBottom:8}}/>
            <div style={{alignSelf:"flex-end"}}><button onClick={()=>setMobileSb(false)} style={{border:"none",background:"none",fontSize:20,color:B.wg,cursor:"pointer",padding:10,minWidth:44,minHeight:44,display:"flex",alignItems:"center",justifyContent:"center"}}>{"\u2715"}</button></div>
          </div>}

          <div style={{flex:1,overflow:"auto",padding:20}}>
            {/* tabs */}
            <div style={{display:"flex",gap:0,marginBottom:20,borderBottom:`1px solid ${B.wgP}`,overflowX:"auto",WebkitOverflowScrolling:"touch",scrollbarWidth:"none"}}>
              {[{id:"refine",l:"Refine"},{id:"quotes",l:isMobile?"Quotes":"Quotes & Poems"},{id:"versions",l:"Versions"},{id:"photos",l:"Photos"},{id:"notes",l:"Notes"}].map(t=>(
                <button key={t.id} onClick={()=>setPanel(t.id)} style={{flex:"1 0 auto",padding:"10px 8px 12px",border:"none",background:"none",cursor:"pointer",fontSize:13,fontWeight:panel===t.id?600:400,color:panel===t.id?B.navy:B.wg,borderBottom:panel===t.id?`2px solid ${B.gold}`:"2px solid transparent",transition:"all .2s",fontFamily:"'Inter',system-ui,sans-serif",whiteSpace:"nowrap",minHeight:44}}>{t.l}</button>
              ))}
            </div>

            {/* REFINE TAB */}
            {panel==="refine"&&<div>
              {finalisedAt && <div style={{marginBottom:16,padding:14,background:B.goldFaint,border:`1px solid ${B.gold}44`,borderRadius:8,fontSize:13,lineHeight:1.5,color:B.navy,fontFamily:"'Inter',system-ui,sans-serif"}}>
                <div style={{fontWeight:600,marginBottom:6}}>This piece is finalised</div>
                <div style={{color:B.txM,marginBottom:10}}>Refining and regenerating are locked. Un-finalise to make changes, or keep making small edits directly in the text.</div>
                <button onClick={handleUnfinalise} disabled={finalising} style={{background:"transparent",border:"none",color:B.navy,fontSize:13,fontWeight:500,textDecoration:"underline",cursor:finalising?"wait":"pointer",padding:0,fontFamily:"'Inter',system-ui,sans-serif",opacity:finalising?.5:1}}>
                  {finalising?"Working\u2026":"Un-finalise this piece"}
                </button>
              </div>}
              {!finalisedAt && <>
                <p style={{fontSize:13,color:B.txM,marginBottom:16,lineHeight:1.6}}>Ask Cyrano to adjust the tone or make specific changes to this draft.</p>

                {/* TONE & LENGTH dropdown - 2026-05-10 - replaced 6-button grid */}
                <div style={{marginBottom:20}}>
                  <div style={{fontSize:11,fontWeight:600,textTransform:"uppercase",letterSpacing:.8,color:B.wg,marginBottom:10}}>Tone & length</div>
                  <select
                    value={selTone||""}
                    onChange={e=>setSelTone(e.target.value||null)}
                    style={{
                      width:"100%",padding:"12px 14px",border:`1px solid ${selTone?B.gold:B.wgP}`,borderRadius:8,
                      background:selTone?B.goldFaint:B.w,fontSize:16,color:B.tx,
                      fontFamily:"'Inter',system-ui,sans-serif",cursor:"pointer",
                      WebkitAppearance:"menulist",appearance:"menulist",minHeight:44,boxSizing:"border-box",
                    }}
                  >
                    <option value="">Choose an adjustment...</option>
                    <optgroup label="Tone">
                      {TONES.filter(t=>t.group==="tone").map(t=>(
                        <option key={t.id} value={t.id}>{t.label}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Length">
                      {TONES.filter(t=>t.group==="length").map(t=>(
                        <option key={t.id} value={t.id}>{t.label}</option>
                      ))}
                    </optgroup>
                  </select>
                  {selTone&&<button onClick={()=>handleGen("tone",TONES.find(t=>t.id===selTone)?.label || "")} disabled={generating} style={{
                    width:"100%",marginTop:10,padding:"11px 16px",minHeight:44,background:generating?B.wgP:B.navy,color:B.w,
                    border:"none",borderRadius:8,fontSize:14,fontWeight:500,cursor:generating?"wait":"pointer",transition:"background .2s",fontFamily:"'Inter',system-ui,sans-serif",
                  }}>
                    {generating?genMsg:`Apply: ${TONES.find(t=>t.id===selTone)?.label}`}
                  </button>}
                </div>

                {/* IN THE STYLE OF - 2026-05-10 - new section */}
                <div style={{marginBottom:20,borderTop:`1px solid ${B.wgP}`,paddingTop:16}}>
                  <div style={{fontSize:11,fontWeight:600,textTransform:"uppercase",letterSpacing:.8,color:B.wg,marginBottom:10}}>In the style of...</div>
                  <input
                    type="text"
                    value={selStyle}
                    onChange={e=>setSelStyle(e.target.value)}
                    placeholder="e.g. Tim Winton, Maya Angelou, Dylan Thomas"
                    style={{
                      width:"100%",padding:"12px 14px",border:`1px solid ${B.wgP}`,borderRadius:8,
                      fontSize:16,fontFamily:"'Inter',system-ui,sans-serif",color:B.tx,background:B.w,
                      boxSizing:"border-box",minHeight:44,
                    }}
                  />
                  <p style={{fontSize:12,color:B.txM,marginTop:8,marginBottom:0,lineHeight:1.5}}>
                    Cyrano will channel their sensibility - cadence, vocabulary, rhythm - while keeping the piece appropriate to a memorial register. Best for distinctive writers or speakers.
                  </p>
                  {selStyle.trim()&&<button onClick={()=>handleGen("style",selStyle.trim())} disabled={generating} style={{
                    width:"100%",marginTop:10,padding:"11px 16px",minHeight:44,background:generating?B.wgP:B.navy,color:B.w,
                    border:"none",borderRadius:8,fontSize:14,fontWeight:500,cursor:generating?"wait":"pointer",fontFamily:"'Inter',system-ui,sans-serif",
                  }}>
                    {generating?genMsg:`Apply style: ${selStyle.trim()}`}
                  </button>}
                </div>

                <div style={{borderTop:`1px solid ${B.wgP}`,paddingTop:16}}>
                  <div style={{fontSize:11,fontWeight:600,textTransform:"uppercase",letterSpacing:.8,color:B.wg,marginBottom:10}}>Tell Cyrano what to change</div>
                  <textarea value={customReq} onChange={e=>setCustomReq(e.target.value)}
                    placeholder="e.g. Make the opening more personal, add the fishing trip story, soften the ending\u2026"
                    rows={4} style={{
                      width:"100%",padding:12,border:`1px solid ${B.wgP}`,borderRadius:8,resize:"vertical",
                      fontSize:14,fontFamily:"'Georgia',serif",lineHeight:1.6,color:B.tx,background:B.w,boxSizing:"border-box",
                    }}/>
                  {customReq.trim()&&<button onClick={()=>handleGen("custom",customReq)} disabled={generating} style={{
                    width:"100%",marginTop:8,padding:"11px 16px",background:generating?B.wgP:B.navy,color:B.w,
                    border:"none",borderRadius:8,fontSize:14,fontWeight:500,cursor:generating?"wait":"pointer",fontFamily:"'Inter',system-ui,sans-serif",
                  }}>
                    {generating?genMsg:"Generate new version"}
                  </button>}
                </div>
              </>}
            </div>}

            {/* QUOTES & POEMS TAB */}
            {panel==="quotes"&&<div>
              {/* Sub-toggle: Quotes vs Poems */}
              <div style={{display:"flex",gap:0,marginBottom:16,background:B.w,borderRadius:8,border:`1px solid ${B.wgP}`,overflow:"hidden"}}>
                <button onClick={()=>setQpSection("quotes")} style={{
                  flex:1,padding:"10px 0",minHeight:44,border:"none",fontSize:14,fontWeight:qpSection==="quotes"?600:400,
                  background:qpSection==="quotes"?B.navy:"transparent",color:qpSection==="quotes"?B.w:B.wg,
                  cursor:"pointer",transition:"all .2s",fontFamily:"'Inter',system-ui,sans-serif",
                }}>Quotes</button>
                <button onClick={()=>setQpSection("poems")} style={{
                  flex:1,padding:"10px 0",minHeight:44,border:"none",borderLeft:`1px solid ${B.wgP}`,fontSize:14,fontWeight:qpSection==="poems"?600:400,
                  background:qpSection==="poems"?B.navy:"transparent",color:qpSection==="poems"?B.w:B.wg,
                  cursor:"pointer",transition:"all .2s",fontFamily:"'Inter',system-ui,sans-serif",
                }}>Poems</button>
              </div>

              {/* ---- QUOTES SECTION ---- */}
              {qpSection==="quotes"&&<>
                <p style={{fontSize:14,color:B.txM,marginBottom:16,lineHeight:1.6}}>
                  Cyrano will suggest quotes to weave into your {docType}, matched to {displayFirst ? displayFirst + "'s" : "their"} character and story.
                </p>

                <button
                  onClick={handleSuggestQuotes}
                  disabled={quotesLoading}
                  style={{
                    width:"100%",padding:"12px 16px",marginBottom:20,
                    background:quotesLoading?B.wgP:B.navy,color:B.w,
                    border:"none",borderRadius:8,fontSize:14,fontWeight:500,
                    cursor:quotesLoading?"wait":"pointer",transition:"background .2s",
                    fontFamily:"'Inter',system-ui,sans-serif",
                    minHeight:44,display:"flex",alignItems:"center",justifyContent:"center",gap:8,
                  }}
                >
                  {quotesLoading ? (
                    <>
                      <span style={{display:"inline-block",width:16,height:16,border:`2px solid ${B.w}40`,borderTopColor:B.w,borderRadius:"50%",animation:"spin 1s linear infinite"}}/>
                      Finding quotes...
                    </>
                  ) : quoteSuggestions.length > 0 ? "Suggest new quotes" : "Suggest a quote"}
                </button>

                {quotesError && (
                  <div style={{padding:"12px 14px",background:"#fde8e8",borderRadius:8,marginBottom:16,fontSize:14,color:B.err,lineHeight:1.5}}>
                    {quotesError}
                  </div>
                )}

                {quoteSuggestions.map((q, i) => (
                  <div key={i} style={{
                    marginBottom:14,padding:16,background:B.w,borderRadius:10,
                    border:`1px solid ${B.wgP}`,transition:"all .15s",
                  }}>
                    <div style={{fontSize:12,fontWeight:600,textTransform:"uppercase",letterSpacing:.5,color:B.gold,marginBottom:8}}>
                      {q.theme}
                    </div>
                    <div style={{
                      fontSize:14,color:B.navy,fontFamily:"'Georgia',serif",fontStyle:"italic",
                      lineHeight:1.65,marginBottom:8,borderLeft:`3px solid ${B.gold}30`,paddingLeft:12,
                    }}>
                      "{q.quote}"
                    </div>
                    <div style={{fontSize:13,color:B.txM,marginBottom:10}}>
                      — {q.author}{q.source ? `, ${q.source}` : ""}
                    </div>
                    <div style={{
                      fontSize:14,color:B.txM,fontFamily:"'Georgia',serif",
                      lineHeight:1.6,marginBottom:12,padding:"10px 12px",
                      background:B.goldFaint,borderRadius:6,
                    }}>
                      <div style={{fontSize:11,fontWeight:600,textTransform:"uppercase",letterSpacing:.5,color:B.wg,marginBottom:6}}>
                        {q.placement === "closing" ? "Best for closing" : q.placement === "opening" ? "Best for opening" : q.placement === "transition" ? "Good as a transition" : "Works inline"}
                      </div>
                      {q.bridging}
                    </div>
                    {q.placementNote && (
                      <div style={{fontSize:13,color:B.wgL,marginBottom:10,fontStyle:"italic"}}>
                        Tip: {q.placementNote}
                      </div>
                    )}
                    <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                      <button
                        onClick={() => insertQuoteAtPlacement(q, i)}
                        style={{
                          flex:1,padding:"10px 12px",minHeight:44,minWidth:120,
                          background:quoteInserted===i?B.ok:B.navy,color:B.w,
                          border:"none",borderRadius:8,fontSize:14,fontWeight:500,
                          cursor:"pointer",transition:"all .2s",fontFamily:"'Inter',system-ui,sans-serif",
                        }}
                      >
                        {quoteInserted===i ? "Inserted \u2713" : "Insert into text"}
                      </button>
                      <button
                        onClick={() => { navigator.clipboard.writeText(q.bridging); setQuoteCopiedClip(i); setTimeout(()=>setQuoteCopiedClip(null),2000); }}
                        style={{
                          padding:"10px 14px",minHeight:44,
                          background:B.w,color:B.navy,
                          border:`1px solid ${B.wgP}`,borderRadius:8,fontSize:14,fontWeight:500,
                          cursor:"pointer",transition:"all .2s",fontFamily:"'Inter',system-ui,sans-serif",
                        }}
                      >
                        {quoteCopiedClip===i ? "Copied \u2713" : "Copy"}
                      </button>
                    </div>
                  </div>
                ))}

                {quoteSuggestions.length > 0 && (
                  <p style={{fontSize:13,color:B.wgL,textAlign:"center",marginTop:8,lineHeight:1.5}}>
                    Quotes are matched to the content of your {docType}. Edit your text and suggest again for different results.
                  </p>
                )}
              </>}

              {/* ---- POEMS SECTION ---- */}
              {qpSection==="poems"&&<>
                <p style={{fontSize:14,color:B.txM,marginBottom:16,lineHeight:1.6}}>
                  Poems are read separately at the service — often by a family member or friend. Cyrano will suggest poems suited to {displayFirst || "your loved one"}.
                </p>

                <button
                  onClick={handleSuggestPoems}
                  disabled={poemsLoading}
                  style={{
                    width:"100%",padding:"12px 16px",marginBottom:20,
                    background:poemsLoading?B.wgP:B.navy,color:B.w,
                    border:"none",borderRadius:8,fontSize:14,fontWeight:500,
                    cursor:poemsLoading?"wait":"pointer",transition:"background .2s",
                    fontFamily:"'Inter',system-ui,sans-serif",
                    minHeight:44,display:"flex",alignItems:"center",justifyContent:"center",gap:8,
                  }}
                >
                  {poemsLoading ? (
                    <>
                      <span style={{display:"inline-block",width:16,height:16,border:`2px solid ${B.w}40`,borderTopColor:B.w,borderRadius:"50%",animation:"spin 1s linear infinite"}}/>
                      Finding poems...
                    </>
                  ) : poemSuggestions.length > 0 ? "Suggest new poems" : "Suggest a poem"}
                </button>

                {poemsError && (
                  <div style={{padding:"12px 14px",background:"#fde8e8",borderRadius:8,marginBottom:16,fontSize:14,color:B.err,lineHeight:1.5}}>
                    {poemsError}
                  </div>
                )}

                {poemSuggestions.map((p, i) => (
                  <div key={i} style={{
                    marginBottom:16,padding:16,background:B.w,borderRadius:10,
                    border:`1px solid ${B.wgP}`,transition:"all .15s",
                  }}>
                    {/* Mood tag */}
                    <div style={{fontSize:12,fontWeight:600,textTransform:"uppercase",letterSpacing:.5,color:B.gold,marginBottom:8}}>
                      {p.mood}
                    </div>

                    {/* Title and author */}
                    <div style={{fontSize:16,color:B.navy,fontFamily:"'Playfair Display','Georgia',serif",fontWeight:600,marginBottom:2}}>
                      {p.title}
                    </div>
                    <div style={{fontSize:13,color:B.txM,marginBottom:12}}>
                      {p.author}{p.collection ? ` — ${p.collection}` : ""}
                    </div>

                    {/* Full poem text (public domain) or excerpt (in-copyright) */}
                    {p.copyright === "public_domain" && p.fullText ? (
                      <div style={{
                        fontSize:14,color:B.navy,fontFamily:"'Georgia',serif",
                        lineHeight:1.8,marginBottom:12,padding:"14px 16px",
                        background:"#fafaf7",borderRadius:8,borderLeft:`3px solid ${B.gold}`,
                        maxHeight:200,overflow:"auto",
                      }}>
                        {p.fullText.replace(/\\n/g, '\n').split('\n').map((line: string, li: number) => (
                          <div key={li} style={{minHeight:line.trim() ? "auto" : 12}}>{line || "\u00A0"}</div>
                        ))}
                      </div>
                    ) : (
                      <div style={{
                        fontSize:14,color:B.navy,fontFamily:"'Georgia',serif",fontStyle:"italic",
                        lineHeight:1.65,marginBottom:8,borderLeft:`3px solid ${B.wgP}`,paddingLeft:12,
                      }}>
                        "{p.excerpt}"
                      </div>
                    )}

                    {/* Copyright notice for in-copyright poems */}
                    {p.copyright === "in_copyright" && (
                      <div style={{
                        fontSize:14,color:B.wg,marginBottom:12,padding:"10px 12px",
                        background:"#fff8ee",borderRadius:6,border:`1px solid #f0e0c0`,lineHeight:1.5,
                      }}>
                        This poem is under copyright. You can read the full text or purchase the collection using the links below.
                      </div>
                    )}

                    {/* Connection to the deceased */}
                    <div style={{
                      fontSize:14,color:B.txM,fontFamily:"'Georgia',serif",
                      lineHeight:1.6,marginBottom:12,padding:"10px 12px",
                      background:B.goldFaint,borderRadius:6,
                    }}>
                      <div style={{fontSize:11,fontWeight:600,textTransform:"uppercase",letterSpacing:.5,color:B.wg,marginBottom:6}}>
                        Why this poem
                      </div>
                      {p.connection}
                    </div>

                    {/* Who should read it */}
                    <div style={{fontSize:14,color:B.txM,marginBottom:8,lineHeight:1.5}}>
                      <strong style={{color:B.navy,fontWeight:600}}>Who should read this:</strong> {p.whoShouldRead}
                    </div>

                    {/* Reading guidance */}
                    <div style={{fontSize:13,color:B.wgL,marginBottom:12,fontStyle:"italic",lineHeight:1.5}}>
                      Reading note: {p.readingNote}
                    </div>

                    {/* Actions */}
                    <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                      {/* Print — for handing to the reader at the service */}
                      <button
                        onClick={() => printPoem(p)}
                        style={{
                          flex:1,padding:"10px 12px",minHeight:44,minWidth:100,
                          background:B.navy,color:B.w,
                          border:"none",borderRadius:8,fontSize:14,fontWeight:500,
                          cursor:"pointer",transition:"all .2s",fontFamily:"'Inter',system-ui,sans-serif",
                          display:"flex",alignItems:"center",justifyContent:"center",gap:6,
                        }}
                      >
                        <img src="/Assets/200-handdrawn-icons-by-heloholo/png/48/print48.png" alt="" style={{width:18,height:18,filter:"brightness(0) invert(1)"}} onError={(e)=>{(e.target as HTMLImageElement).style.display="none"}}/>
                        Print for reader
                      </button>
                      {/* Copy — to paste into an email/message to the reader */}
                      <button
                        onClick={() => copyPoemForSharing(p, i)}
                        style={{
                          padding:"10px 14px",minHeight:44,
                          background:poemCopied===i?B.ok:B.w,color:poemCopied===i?B.w:B.navy,
                          border:`1px solid ${poemCopied===i?B.ok:B.wgP}`,borderRadius:8,fontSize:14,fontWeight:500,
                          cursor:"pointer",transition:"all .2s",fontFamily:"'Inter',system-ui,sans-serif",
                        }}
                      >
                        {poemCopied===i ? "Copied \u2713" : "Copy to share"}
                      </button>
                    </div>

                    {/* Links for in-copyright poems */}
                    {p.copyright === "in_copyright" && p.links && (
                      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:10}}>
                        <a
                          href={p.links.poetryFoundation}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            flex:1,padding:"10px 12px",minHeight:44,minWidth:100,
                            background:"transparent",color:B.navy,textDecoration:"none",
                            border:`1px solid ${B.wgP}`,borderRadius:8,fontSize:14,fontWeight:500,
                            fontFamily:"'Inter',system-ui,sans-serif",textAlign:"center",
                            display:"flex",alignItems:"center",justifyContent:"center",
                          }}
                        >
                          Read full poem
                        </a>
                        <a
                          href={p.links.amazon}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            flex:1,padding:"10px 12px",minHeight:44,minWidth:100,
                            background:"transparent",color:B.navy,textDecoration:"none",
                            border:`1px solid ${B.wgP}`,borderRadius:8,fontSize:14,fontWeight:500,
                            fontFamily:"'Inter',system-ui,sans-serif",textAlign:"center",
                            display:"flex",alignItems:"center",justifyContent:"center",
                          }}
                        >
                          Buy the collection
                        </a>
                      </div>
                    )}
                  </div>
                ))}

                {poemSuggestions.length > 0 && (
                  <p style={{fontSize:13,color:B.wgL,textAlign:"center",marginTop:8,lineHeight:1.5}}>
                    Poems are matched to the content of your {docType}. Edit your text and suggest again for different results.
                  </p>
                )}
              </>}

              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>}

            {/* VERSIONS TAB */}
            {panel==="versions"&&<div>
              <p style={{fontSize:13,color:B.txM,marginBottom:16,lineHeight:1.6}}>Every version is saved. Switch between them to compare, or continue editing from any point.</p>
              {versions.length === 0 ? (
                <p style={{fontSize:13,color:B.wgL,textAlign:"center",padding:20}}>No versions yet</p>
              ) : versions.map((v,i)=>(
                <button key={v.id} onClick={()=>switchVersion(i)} style={{
                  width:"100%",padding:"14px",marginBottom:6,border:`1px solid ${v.active?B.gold:B.wgP}`,
                  borderRadius:10,background:v.active?B.goldFaint:B.w,cursor:"pointer",textAlign:"left",
                  transition:"all .15s",display:"block",fontFamily:"'Inter',system-ui,sans-serif",
                }}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                    <span style={{fontSize:14,fontWeight:v.active?600:400,color:B.navy}}>{v.label}</span>
                    {v.active&&<span style={{fontSize:10,fontWeight:600,textTransform:"uppercase",letterSpacing:.8,color:B.ok,background:B.okBg,padding:"2px 8px",borderRadius:10}}>Active</span>}
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:B.wg}}>
                    <span>{v.time}</span><span>{v.words} words</span>
                  </div>
                </button>
              ))}
            </div>}

            {/* NOTES TAB */}
            {/* PHOTOS TAB */}
            {panel==="photos"&&<div>
              {testamentLicenseId ? (
                <PhotoGalleryPanel testamentLicenseId={testamentLicenseId} />
              ) : (
                <p style={{fontSize:13,color:B.wgL,lineHeight:1.6}}>Photos will be available once a testament license is linked.</p>
              )}
            </div>}

            {panel==="notes"&&<div>
              {/* collaborators */}
              {contributors.length > 0 && <div style={{display:"flex",alignItems:"center",marginBottom:18}}>
                {contributors.map((c,i)=><div key={i} style={{width:28,height:28,borderRadius:"50%",background:[B.navy,B.lavender,B.gold][i%3],color:i===0?B.w:B.navy,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:600,marginLeft:i>0?-5:0,border:`2px solid ${B.w}`,position:"relative",zIndex:contributors.length-i,fontFamily:"'Inter',system-ui,sans-serif"}}>{c.initials}</div>)}
                <span style={{marginLeft:10,fontSize:12,color:B.wg}}>{contributors.length} contributor{contributors.length!==1?"s":""}</span>
              </div>}

              {notes.length === 0 ? (
                <p style={{fontSize:13,color:B.wgL,lineHeight:1.6}}>Notes from your conversation and contributors will appear here once available.</p>
              ) : notes.map((sec,si)=>(
                <div key={si} style={{marginBottom:22}}>
                  <div style={{fontSize:12,fontWeight:600,color:B.navy,marginBottom:8,textTransform:"uppercase",letterSpacing:.5}}>{sec.title}</div>
                  {sec.items.map((item,ii)=>(
                    <div key={ii} style={{fontSize:13,color:B.txM,padding:"7px 11px",background:B.w,borderRadius:6,marginBottom:4,lineHeight:1.55,fontFamily:"'Georgia',serif",borderLeft:`3px solid ${sec.colour}30`}}>{item}</div>
                  ))}
                </div>
              ))}

              <div style={{marginTop:24,padding:14,background:`${B.navy}06`,borderRadius:8,border:`1px solid ${B.navy}10`}}>
                <div style={{fontSize:11,fontWeight:600,color:B.navy,marginBottom:8,textTransform:"uppercase",letterSpacing:.5}}>Speaking guide</div>
                <div style={{fontSize:12,color:B.txM,lineHeight:1.7}}>
                  <div style={{marginBottom:3}}><span style={{display:"inline-block",width:8,height:8,borderRadius:"50%",background:B.gold,marginRight:7,verticalAlign:"middle"}}/>Eulogy: 5\u20137 min (800\u20131,000 words)</div>
                  <div><span style={{display:"inline-block",width:8,height:8,borderRadius:"50%",background:B.lavender,marginRight:7,verticalAlign:"middle"}}/>Tribute: ~3 min (400\u2013500 words)</div>
                </div>
              </div>
            </div>}

            {/* action buttons */}
            <div style={{marginTop:24,paddingTop:16,borderTop:`1px solid ${B.wgP}`}}>
              <button onClick={goToConversation} style={{width:"100%",padding:"12px 16px",background:B.w,color:B.navy,border:`1px solid ${B.navy}22`,borderRadius:10,fontSize:14,fontWeight:500,cursor:"pointer",marginBottom:8,fontFamily:"'Inter',system-ui,sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                <img src="/Assets/200-handdrawn-icons-by-heloholo/png/48/comment148.png" alt="" style={{width:16,height:16,opacity:.6}}/> Continue Conversation
              </button>
              {currentSessionId&&<button onClick={goToFamilyMemories} style={{width:"100%",padding:"12px 16px",background:B.w,color:B.navy,border:`1px solid ${B.navy}22`,borderRadius:10,fontSize:14,fontWeight:500,cursor:"pointer",marginBottom:8,fontFamily:"'Inter',system-ui,sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                <img src="/Assets/200-handdrawn-icons-by-heloholo/png/48/add-contact48.png" alt="" style={{width:16,height:16,opacity:.6}}/> Family &amp; friends' memories
              </button>}
              {/* Funeral Guide pathway from the eulogy workspace — the natural
                  moment to plan the service is while (or after) writing. The
                  guide page itself shows either the content or the purchase
                  card, so this button is always safe to offer. */}
              {testamentLicenseId&&<button onClick={()=>{ window.location.href = `/funeral-guide/${testamentLicenseId}`; }} style={{width:"100%",padding:"12px 16px",background:B.w,color:B.navy,border:`1px solid ${B.navy}22`,borderRadius:10,fontSize:14,fontWeight:500,cursor:"pointer",marginBottom:8,fontFamily:"'Inter',system-ui,sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                <img src="/Assets/200-handdrawn-icons-by-heloholo/png/48/list48.png" alt="" style={{width:16,height:16,opacity:.6}}/> Plan the service
              </button>}
              <button onClick={()=>{ setGenMsg(""); setShowExportDialog(true); }} style={{width:"100%",padding:"12px 16px",background:B.w,color:B.navy,border:`1px solid ${B.navy}22`,borderRadius:10,fontSize:14,fontWeight:500,cursor:"pointer",marginBottom:8,fontFamily:"'Inter',system-ui,sans-serif"}}>Export draft</button>
              {!archivedAt && !finalisedAt && <button onClick={()=>setShowFinaliseConfirm(true)} disabled={finalising||!currentSessionId||!testamentLicenseId} style={{width:"100%",padding:"14px 16px",background:B.navy,color:B.w,border:"none",borderRadius:10,fontSize:15,fontWeight:600,cursor:finalising?"wait":"pointer",fontFamily:"'Inter',system-ui,sans-serif",letterSpacing:.3,opacity:(finalising||!currentSessionId||!testamentLicenseId)?.5:1}}>Finalise &amp; Complete</button>}
              {finalisedAt && <>
                <button onClick={handleDownloadMemoryCollection} disabled={mcDownloading} style={{width:"100%",padding:"14px 16px",background:B.navy,color:B.w,border:"none",borderRadius:10,fontSize:15,fontWeight:600,cursor:mcDownloading?"wait":"pointer",fontFamily:"'Inter',system-ui,sans-serif",letterSpacing:.3,opacity:mcDownloading?.6:1,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                  <img src="/Assets/200-handdrawn-icons-by-heloholo/png/48/download48.png" alt="" style={{width:16,height:16,filter:"invert(1) brightness(2)"}}/>
                  {mcDownloading?"Preparing\u2026":"Download Memory Collection"}
                </button>
                <button onClick={handleUnfinalise} disabled={finalising} style={{width:"100%",marginTop:8,padding:"10px 16px",background:"transparent",color:B.txM,border:"none",fontSize:13,fontWeight:500,cursor:finalising?"wait":"pointer",fontFamily:"'Inter',system-ui,sans-serif",textDecoration:"underline",opacity:finalising?.5:1}}>
                  {finalising?"Working\u2026":"Un-finalise this piece"}
                </button>
              </>}
            </div>
          </div>
        </div>}
      </div>

      {/* MOBILE BOTTOM ACTION BAR */}
      {isMobile&&!mobileSb&&!focus&&<div className="no-print" style={{position:"fixed",left:0,right:0,bottom:0,zIndex:50,background:B.w,borderTop:`1px solid ${B.wgP}`,paddingTop:8,paddingLeft:12,paddingRight:12,paddingBottom:"max(10px, env(safe-area-inset-bottom))",display:"flex",flexDirection:"column",gap:8,boxShadow:"0 -2px 12px rgba(28,41,81,0.06)"}}>
        {/* Row 1: word count + full speaking time, centred. Speaking time keeps the
            pacing colour (green in range, red over) so the celebrant can gauge length. */}
        <div style={{textAlign:"center",fontSize:12.5,color:B.wg,lineHeight:1.2,letterSpacing:".2px",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
          <strong style={{color:B.navy,fontWeight:600}}>{wc.toLocaleString()}</strong> words
          <span style={{margin:"0 7px",color:B.wgP}}>·</span>
          <strong style={{color:ov?B.err:inR?B.ok:B.navy,fontWeight:600}}>{ft(wc)}</strong> speaking
        </div>
        {/* Row 2: equal-width actions */}
        <div style={{display:"flex",gap:9}}>
          <button onClick={()=>setMobileSb(true)} style={{flex:1,minHeight:46,padding:"0 8px",background:B.navy,color:B.w,border:"none",borderRadius:10,fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',system-ui,sans-serif"}}>Tools</button>
          <button onClick={()=>{ setGenMsg(""); setShowExportDialog(true); }} style={{flex:1,minHeight:46,padding:"0 8px",background:B.w,color:B.navy,border:`1px solid ${B.navy}33`,borderRadius:10,fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',system-ui,sans-serif"}}>Export</button>
          {!archivedAt && !finalisedAt && <button onClick={()=>setShowFinaliseConfirm(true)} disabled={finalising||!currentSessionId||!testamentLicenseId} style={{flex:1,minHeight:46,padding:"0 8px",background:B.navy,color:B.w,border:"none",borderRadius:10,fontSize:14,fontWeight:600,cursor:finalising?"wait":"pointer",fontFamily:"'Inter',system-ui,sans-serif",opacity:(finalising||!currentSessionId||!testamentLicenseId)?.5:1}}>Finalise</button>}
        {finalisedAt && <button onClick={handleDownloadMemoryCollection} disabled={mcDownloading} style={{flex:1,minHeight:46,padding:"0 8px",background:B.navy,color:B.w,border:"none",borderRadius:10,fontSize:13,fontWeight:600,lineHeight:1.15,cursor:mcDownloading?"wait":"pointer",fontFamily:"'Inter',system-ui,sans-serif",opacity:mcDownloading?.6:1}}>{mcDownloading?"\u2026":"Memory Collection"}</button>}
        </div>
      </div>}

      {/* FINALISE CONFIRM MODAL */}
      {showFinaliseConfirm && <div className="no-print" style={{position:"fixed",inset:0,zIndex:1100,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(0,0,0,.55)"}} onClick={()=>!finalising&&setShowFinaliseConfirm(false)}>
        <div onClick={e=>e.stopPropagation()} style={{background:B.cream,borderRadius:16,width:"95%",maxWidth:460,padding:isMobile?22:30,boxShadow:"0 20px 60px rgba(0,0,0,.3)",fontFamily:"'Inter',system-ui,sans-serif"}}>
          <h2 style={{margin:"0 0 12px",fontSize:22,fontFamily:"'Playfair Display',Georgia,serif",color:B.navy,lineHeight:1.2}}>Finalise this {docType}?</h2>
          <p style={{margin:"0 0 14px",fontSize:14,lineHeight:1.55,color:B.tx}}>
            This locks the current version as your final piece and unlocks your <strong>Memory Collection</strong> {"\u2014"} a keepsake PDF with the finalised text, every photo you've uploaded, and every memory shared by your contributors.
          </p>
          <div style={{padding:12,background:B.w,border:`1px solid ${B.wgP}`,borderRadius:8,marginBottom:18,fontSize:13,lineHeight:1.55,color:B.txM}}>
            <div style={{marginBottom:6}}><strong style={{color:B.navy,fontWeight:600}}>What changes:</strong> Generate, Refine and Top-up get locked.</div>
            <div style={{marginBottom:6}}><strong style={{color:B.navy,fontWeight:600}}>What stays open:</strong> You can keep editing the text directly here.</div>
            <div><strong style={{color:B.navy,fontWeight:600}}>Not permanent:</strong> You can Un-finalise anytime to unlock the tools again.</div>
          </div>
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            <button onClick={()=>!finalising&&setShowFinaliseConfirm(false)} disabled={finalising} style={{flex:"1 1 auto",minWidth:120,padding:"12px 18px",background:B.w,color:B.navy,border:`1px solid ${B.navy}33`,borderRadius:8,fontSize:14,fontWeight:500,cursor:finalising?"not-allowed":"pointer",fontFamily:"'Inter',system-ui,sans-serif",opacity:finalising?.5:1}}>Cancel</button>
            <button onClick={handleFinalise} disabled={finalising} style={{flex:"1 1 auto",minWidth:160,padding:"12px 18px",background:B.navy,color:B.w,border:"none",borderRadius:8,fontSize:14,fontWeight:600,cursor:finalising?"wait":"pointer",fontFamily:"'Inter',system-ui,sans-serif",letterSpacing:.3,opacity:finalising?.7:1}}>
              {finalising?"Finalising\u2026":"Finalise & Complete"}
            </button>
          </div>
        </div>
      </div>}

      {/* REGENERATE CONFIRM / TOP-UP */}
      {showRegenConfirm && <div className="no-print" style={{position:"fixed",inset:0,zIndex:1100,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(0,0,0,.55)"}} onClick={()=>!regenerating&&setShowRegenConfirm(false)}>
        <div onClick={e=>e.stopPropagation()} style={{background:B.cream,borderRadius:16,width:"95%",maxWidth:460,padding:isMobile?22:30,boxShadow:"0 20px 60px rgba(0,0,0,.3)",fontFamily:"'Inter',system-ui,sans-serif"}}>
          {genCycles && genCycles.remaining<=0 ? (<>
            <h2 style={{margin:"0 0 12px",fontSize:22,fontFamily:"'Playfair Display',Georgia,serif",color:B.navy,lineHeight:1.2}}>You've used all your generations</h2>
            <p style={{margin:"0 0 18px",fontSize:14,lineHeight:1.55,color:B.tx}}>
              You've created {genCycles.total} {genCycles.total===1?"version":"versions"} of this {docType}. Add 15 more generations for <strong>$9.95</strong> to keep refining. Every version you've made is safe in the <strong>Versions</strong> tab.
            </p>
            <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
              <button onClick={()=>setShowRegenConfirm(false)} style={{flex:"1 1 auto",minWidth:120,padding:"12px 18px",background:B.w,color:B.navy,border:`1px solid ${B.navy}33`,borderRadius:8,fontSize:14,fontWeight:500,cursor:"pointer",fontFamily:"'Inter',system-ui,sans-serif"}}>Close</button>
              <button onClick={handleTopup} style={{flex:"1 1 auto",minWidth:180,padding:"12px 18px",background:B.navy,color:B.w,border:"none",borderRadius:8,fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',system-ui,sans-serif",letterSpacing:.3}}>Add 15 generations &mdash; $9.95</button>
            </div>
          </>) : (<>
            <h2 style={{margin:"0 0 12px",fontSize:22,fontFamily:"'Playfair Display',Georgia,serif",color:B.navy,lineHeight:1.2}}>Generate a fresh version?</h2>
            <p style={{margin:"0 0 14px",fontSize:14,lineHeight:1.55,color:B.tx}}>
              Cyrano will write a brand-new {docType} from your conversation. Your current draft stays safe &mdash; every version is kept in the <strong>Versions</strong> tab, so you can switch back anytime.
            </p>
            {genCycles && <div style={{padding:12,background:B.w,border:`1px solid ${B.wgP}`,borderRadius:8,marginBottom:18,fontSize:13,lineHeight:1.5,color:B.txM}}>
              You have <strong style={{color:B.navy,fontWeight:600}}>{genCycles.remaining}</strong> of <strong style={{color:B.navy,fontWeight:600}}>{genCycles.total}</strong> generations included with this {docType} remaining, and this uses one. If you ever run out, you can add 15 more for <strong style={{color:B.navy,fontWeight:600}}>$9.95</strong> at any time.
            </div>}
            <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
              <button onClick={()=>!regenerating&&setShowRegenConfirm(false)} disabled={regenerating} style={{flex:"1 1 auto",minWidth:120,padding:"12px 18px",background:B.w,color:B.navy,border:`1px solid ${B.navy}33`,borderRadius:8,fontSize:14,fontWeight:500,cursor:regenerating?"not-allowed":"pointer",fontFamily:"'Inter',system-ui,sans-serif",opacity:regenerating?.5:1}}>Cancel</button>
              <button onClick={handleRegenerate} disabled={regenerating} style={{flex:"1 1 auto",minWidth:150,padding:"12px 18px",background:B.navy,color:B.w,border:"none",borderRadius:8,fontSize:14,fontWeight:600,cursor:regenerating?"wait":"pointer",fontFamily:"'Inter',system-ui,sans-serif",letterSpacing:.3,opacity:regenerating?.7:1}}>
                {regenerating?"Regenerating\u2026":"Regenerate"}
              </button>
            </div>
          </>)}
        </div>
      </div>}

      {/* EXPORT DIALOG */}
      {showExportDialog && <div style={{position:"fixed",inset:0,zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(0,0,0,.45)"}} onClick={()=>setShowExportDialog(false)}>
        <div onClick={e=>e.stopPropagation()} style={{background:B.cream,borderRadius:16,width:"95%",maxWidth:520,maxHeight:"90vh",overflow:"auto",padding:isMobile?20:32,boxShadow:"0 20px 60px rgba(0,0,0,.25)",fontFamily:"'Inter',system-ui,sans-serif"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
            <h2 style={{margin:0,fontSize:20,fontFamily:"'Playfair Display',Georgia,serif",color:B.navy}}>Export your {docType}</h2>
            <button onClick={()=>setShowExportDialog(false)} style={{background:"none",border:"none",fontSize:22,cursor:"pointer",color:B.txL,padding:8,minWidth:44,minHeight:44,display:"flex",alignItems:"center",justifyContent:"center"}}>{"\u00D7"}</button>
          </div>

          {/* Optional service details */}
          <div style={{marginBottom:20,padding:16,background:B.w,borderRadius:10,border:`1px solid ${B.wgP}`}}>
            <div style={{fontSize:12,fontWeight:600,color:B.navy,marginBottom:10,textTransform:"uppercase",letterSpacing:.5}}>Service details (optional)</div>
            <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:10}}>
              <div>
                <label style={{fontSize:14,color:B.txM,display:"block",marginBottom:3}}>Service date</label>
                <DateField value={exportFields.serviceDate} onChange={v=>setExportFields(f=>({...f,serviceDate:v}))} placeholder="e.g. Saturday, 5 April 2026" withWeekday/>
              </div>
              <div>
                <label style={{fontSize:14,color:B.txM,display:"block",marginBottom:3}}>Venue</label>
                <input value={exportFields.serviceVenue} onChange={e=>setExportFields(f=>({...f,serviceVenue:e.target.value}))} placeholder="e.g. St Mary's Church" style={{width:"100%",padding:"12px 12px",border:`1px solid ${B.wgP}`,borderRadius:6,fontSize:16,fontFamily:"'Inter',system-ui,sans-serif",boxSizing:"border-box"}}/>
              </div>
            </div>
            {/* Only ask for dates that weren't already captured during onboarding. */}
            {(!datesOnFile.birth || !datesOnFile.passing) && (
              <>
                <div style={{display:"flex",gap:8,marginTop:10,flexWrap:"wrap"}}>
                  {!datesOnFile.birth && (
                    <div style={{flex:1,minWidth:140}}>
                      <label style={{fontSize:14,color:B.txM,display:"block",marginBottom:3}}>Date of birth</label>
                      <DateField value={exportFields.birthDate} onChange={v=>setExportFields(f=>({...f,birthDate:v}))} placeholder="e.g. 2 June 1931"/>
                    </div>
                  )}
                  {!datesOnFile.passing && (
                    <div style={{flex:1,minWidth:140}}>
                      <label style={{fontSize:14,color:B.txM,display:"block",marginBottom:3}}>Date of passing</label>
                      <DateField value={exportFields.deathDate} onChange={v=>setExportFields(f=>({...f,deathDate:v}))} placeholder="e.g. 10 January 2026"/>
                    </div>
                  )}
                </div>
                <div style={{fontSize:13,color:B.txL,marginTop:8}}>These appear on the cover (e.g. "2 June 1931 &ndash; 10 January 2026"). Leave blank to omit.</div>
              </>
            )}
            {(datesOnFile.birth || datesOnFile.passing) && (
              <div style={{fontSize:13,color:B.txL,marginTop:10}}>
                On the cover: <span style={{color:B.txM}}>{[datesOnFile.birth, datesOnFile.passing].filter(Boolean).join(" – ")}</span>
              </div>
            )}
          </div>

          {/* Cover image selection */}
          <div style={{marginBottom:20,padding:16,background:B.w,borderRadius:10,border:`1px solid ${B.wgP}`}}>
            <div style={{fontSize:12,fontWeight:600,color:B.navy,marginBottom:10,textTransform:"uppercase",letterSpacing:.5}}>Cover image</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:10}}>
              {/* Default: Cyrano clouds */}
              <div onClick={()=>setCoverPhotoId(null)} style={{width:84,cursor:"pointer",textAlign:"center"}}>
                <div style={{width:84,height:56,borderRadius:8,overflow:"hidden",border:coverPhotoId===null?`3px solid ${B.navy}`:`3px solid transparent`,opacity:coverPhotoId===null?1:.55,transition:"all .2s",background:`linear-gradient(180deg, #c9c6d4 0%, #e9e7ee 70%, ${B.cream} 100%)`}}/>
                <div style={{fontSize:11,color:coverPhotoId===null?B.navy:B.txL,marginTop:3,fontWeight:coverPhotoId===null?600:400}}>Cyrano clouds</div>
              </div>
              {/* Uploaded photos */}
              {exportPhotos.map(photo=>{
                const sel = coverPhotoId===photo.id;
                return (
                  <div key={photo.id} onClick={()=>setCoverPhotoId(photo.id)} style={{width:84,cursor:"pointer",textAlign:"center"}}>
                    <div style={{width:84,height:56,borderRadius:8,overflow:"hidden",border:sel?`3px solid ${B.navy}`:`3px solid transparent`,opacity:sel?1:.55,transition:"all .2s"}}>
                      <img src={photo.url} alt={photo.caption||""} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                    </div>
                    <div style={{fontSize:11,color:sel?B.navy:B.txL,marginTop:3,fontWeight:sel?600:400}}>{sel?"On cover":"Use photo"}</div>
                  </div>
                );
              })}
            </div>
            <div style={{fontSize:14,color:B.txL,marginTop:8}}>
              {exportPhotos.length>0
                ? "Choose the soft Cyrano clouds, or a photo of your loved one. A photo is cropped to fit the cover and gently fades into the page."
                : "The Cyrano clouds appear on the cover. Upload a photo in the Photos tab to use one of your own instead."}
            </div>
          </div>
          {photosLoading && <div style={{marginBottom:16,fontSize:13,color:B.txL,textAlign:"center"}}>Loading photos...</div>}

          {/* Export format options */}
          <div style={{fontSize:12,fontWeight:600,color:B.navy,marginBottom:10,textTransform:"uppercase",letterSpacing:.5}}>Choose format</div>
          <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:10,marginBottom:16}}>
            {[
              { id:"docx",  label:"Word Document", desc:"Editable .docx file", icon:"/Assets/200-handdrawn-icons-by-heloholo/png/48/file_doc48.png" },
              { id:"gdocs", label:"Google Docs",  desc:"Opens in Google Docs (.docx)", icon:"/Assets/200-handdrawn-icons-by-heloholo/png/48/file_doc48.png" },
              { id:"pdf",   label:"PDF Document",  desc:"Print-ready layout", icon:"/Assets/200-handdrawn-icons-by-heloholo/png/48/file_pdf48.png" },
              { id:"speaker", label:"Speaker's Copy", desc:"Large print for reading aloud (PDF)", icon:"/Assets/200-handdrawn-icons-by-heloholo/png/48/microphone48.png" },
              { id:"pptx", label:"Presentation",   desc:"PowerPoint slides",  icon:"/Assets/200-handdrawn-icons-by-heloholo/png/48/landscape_doc48.png" },
              { id:"txt",  label:"Plain Text",     desc:"Simple text file",   icon:"/Assets/200-handdrawn-icons-by-heloholo/png/48/file_txt48.png" },
            ].map(fmt=>(
              <button key={fmt.id} onClick={()=>handleExportFormat(fmt.id)} disabled={!!exportLoading}
                style={{padding:"14px 16px",background:B.w,border:`1px solid ${exportLoading===fmt.id?B.navy:B.wgP}`,borderRadius:10,cursor:exportLoading?"wait":"pointer",textAlign:"left",transition:"border-color .2s",opacity:exportLoading&&exportLoading!==fmt.id?.5:1,minHeight:44}}>
                <img src={fmt.icon} alt="" style={{width:24,height:24,marginBottom:4,filter:"brightness(0) saturate(100%) invert(15%) sepia(30%) saturate(2000%) hue-rotate(200deg) brightness(90%) contrast(95%)"}} />
                <div style={{fontSize:14,fontWeight:600,color:B.navy}}>{exportLoading===fmt.id?"Generating...":fmt.label}</div>
                <div style={{fontSize:12,color:B.txL,marginTop:2}}>{fmt.desc}</div>
              </button>
            ))}
          </div>

          {/* Social media section */}
          <div style={{fontSize:12,fontWeight:600,color:B.navy,marginBottom:10,textTransform:"uppercase",letterSpacing:.5}}>Social media images</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            {[
              { id:"facebook",        label:"Facebook",        desc:"1200 \u00D7 630px",  icon:"/Assets/200-handdrawn-icons-by-heloholo/png/48/facebook48.png" },
              { id:"instagram",       label:"Instagram",       desc:"1080 \u00D7 1080px", icon:"/Assets/200-handdrawn-icons-by-heloholo/png/48/camera48.png" },
              { id:"instagram-story", label:"Instagram Story", desc:"1080 \u00D7 1920px", icon:"/Assets/200-handdrawn-icons-by-heloholo/png/48/camera48.png" },
            ].map(fmt=>(
              <button key={fmt.id} onClick={()=>handleExportFormat(fmt.id)} disabled={!!exportLoading}
                style={{padding:"14px 16px",background:B.w,border:`1px solid ${exportLoading===fmt.id?B.navy:B.wgP}`,borderRadius:10,cursor:exportLoading?"wait":"pointer",textAlign:"left",transition:"border-color .2s",opacity:exportLoading&&exportLoading!==fmt.id?.5:1,minHeight:44,display:"flex",alignItems:"center",gap:10}}>
                <img src={fmt.icon} alt="" style={{width:20,height:20,filter:"brightness(0) saturate(100%) invert(15%) sepia(30%) saturate(2000%) hue-rotate(200deg) brightness(90%) contrast(95%)",flexShrink:0}} />
                <div>
                  <div style={{fontSize:14,fontWeight:600,color:B.navy}}>{exportLoading===fmt.id?"Generating...":fmt.label}</div>
                  <div style={{fontSize:12,color:B.txL,marginTop:2}}>{fmt.desc}</div>
                </div>
              </button>
            ))}
          </div>

          {genMsg && <div style={{marginTop:12,padding:10,background:`${B.err}10`,color:B.err,borderRadius:8,fontSize:14,textAlign:"center"}}>{genMsg}</div>}
        </div>
      </div>}
    </div>
  );
}

// --- Helpers ---
function getInitials(name: string): string {
  return name.split(" ").map(n => n[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}

function formatTimeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}
